// Imports
import { AccountCircle, Call, ContentCopyRounded, CurrencyRupee, DateRange, Download, Edit, GppBad, GppGood, GppMaybe, Home, LocationOn, MenuBook, Refresh, Star, TaskAlt } from "@mui/icons-material";
import { LoadingButton, TabContext, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator, timelineOppositeContentClasses } from "@mui/lab";
import { Button, Dialog, DialogContent, Drawer, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box, SxProps } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { Fragment, ReactElement, useState } from "react";
import { useSelector } from "react-redux";
import { GobikesApi } from "../../config/Apis";
import { Colors, Urls } from "../../config/Constants";
import { getHubSelection } from "../../features/hubSelection";
import { getAuthCookie } from "../auth/AuthUtil";
import { BookingStatus, OrderModel, OrderBikePriceBreakdown, PaymentMode, PricePackages, PricePackageUnits, RideStatus, ReviewModel, OrderEvent, OrderMode, OrderUploadLabels, AccessoryPricePackages, VehicleType, BookingType, BillingCycle, SubscriptionInvoiceStatus } from "../utils/Models";
import { EndBooking } from "./EndBooking";
import { StartBooking } from "./StartBooking";
import { ExtendBooking } from "./ExtendBooking";
import { CreateCustomPaymentLink } from "./CreateCustomPaymentLink";
import { RecordCashPayment } from "./RecordCashPayment";
import { DamageInvoice } from "./DamageInvoice";
import { CancelBooking } from "./CancelBooking";
import { capitalizeFirstLetter, toIstDateString, computeBillingCycleEnd } from "../utils/CommonUtils";
import { UploadImageViewer } from "./UploadDetail";
import { EditBooking } from "./EditBooking";
import { bookingStatusToBgColor } from "./BookingsDataGrid";
import SwipeableViews from 'react-swipeable-views'
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import DownloadIcon from "../../assets/icons/download.svg";
import VerifiedIcon from "../../assets/icons/verified.svg";

// Local constants
type DocVerificationStatus = "Pending" | "Verified" | "Unverified";
const verificationToColor: { [k in DocVerificationStatus]: string } = {
  Pending: Colors.warningYellow,
  Verified: Colors.successGreen,
  Unverified: Colors.failureRed,
};
const verificationToIcon: { [k in DocVerificationStatus]: ReactElement } = {
  Pending: <GppMaybe />,
  Verified: <GppGood />,
  Unverified: <GppBad />,
};
const rideStatusToColor = {
  [RideStatus.NOT_STARTED]: '#b07d00',
  [RideStatus.STARTED]: Colors.primary,
  [RideStatus.ENDED]: Colors.failureRed,
};
export const RideStatusToText = {
  [RideStatus.NOT_STARTED]: "Not started",
  [RideStatus.STARTED]: "Ongoing",
  [RideStatus.ENDED]: "Ended",
};
const bookingStatusToColor = {
  [BookingStatus.PENDING]: Colors.warningYellow,
  [BookingStatus.FAILED]: Colors.failureRed,
  [BookingStatus.CONFIRMED]: Colors.primary,
  [BookingStatus.EXTENDED]: Colors.successGreen,
  [BookingStatus.CANCELLED]: Colors.failureRed,
};
const packageNameToString = {
  [PricePackages.YEARLY]: 'Yearly package',
  [PricePackages.HALF_YEARLY]: '6 Month package',
  [PricePackages.QUARTER_YEARLY]: '3 Month package',
  [PricePackages.MONTHLY]: 'Monthly package',
  [PricePackages.FORTNIGHTLY]: 'Fortnight package',
  [PricePackages.WEEKLY]: 'Weekly package',
  [PricePackages.DAILY]: 'Daily package',
  [PricePackages.QUARTER_DAY]: '6 Hours package',
  [PricePackages.EIGHTH_DAY]: '3 Hours package',
  [PricePackages.TRIP]: 'Trip package'
}
const unitNameToString = {
  [PricePackageUnits.HOUR_UNIT]: 'Hour',
  [PricePackageUnits.DAY_UNIT]: 'Day',
  [PricePackageUnits.HALF_DAY_UNIT]: 'Half day',
  [PricePackageUnits.WEEKDAY_UNIT]: 'Weekday',
  [PricePackageUnits.WEEKEND_UNIT]: 'Weekend',
  [PricePackageUnits.TRIP_UNIT]: 'Trip'
}
const orderEventToDisplayMapping: { [k in OrderEvent]: { text: string, nodeColor?: 'error' | 'inherit' | 'grey' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | undefined } } = {
  PLACED: { text: 'Order created' },
  PAYMENT: { text: 'Payment received' },
  AMOUNT_UPDATED: { text: 'Amount updated' },
  EXTENDED: { text: 'Booking extended', },
  CANCELLED: { text: 'Booking cancelled', nodeColor: 'error' },
  EDITED: { text: 'Booking updated' },
  VEHICLE_UPDATED: { text: 'Vehicle updated' },
  RIDE_START: { text: 'Pick-up done' },
  RIDE_END: { text: 'Drop-off done' },
  RESCHEDULED: { text: 'Booking rescheduled' },
  PAYMENT_LINK: { text: 'Payment link sent' },
  CUSTOM_PAYMENT_LINK: { text: 'Custom Payment Link Sent' },
  OFFLINE_PAYMENT: { text: 'Offline payment collected', nodeColor: 'info' },
  DAMAGE_INVOICE: { text: 'Damage invoice raised', nodeColor: 'warning' },
  INVOICE_GENERATED: { text: 'Invoice generated', nodeColor: 'info' },
  TICKET_CREATED: { text: 'Support ticket raised', nodeColor: 'warning' },
  TICKET_CLOSED: { text: 'Support ticket closed', nodeColor: 'success' },
  FUTURE_PICKUP_REMARK: { text: 'Future pickup remark', nodeColor: 'info' }
}
const TIMELINE_FILTER_OPTIONS: { label: string, value: string, eventTypes: OrderEvent[] }[] = [
  { label: 'All', value: 'all', eventTypes: [] },
  { label: 'Payment History', value: 'payment', eventTypes: [OrderEvent.PAYMENT, OrderEvent.OFFLINE_PAYMENT] },
  { label: 'Extension History', value: 'extension', eventTypes: [OrderEvent.EXTENDED] },
  { label: 'Invoice History', value: 'invoice', eventTypes: [OrderEvent.INVOICE_GENERATED, OrderEvent.DAMAGE_INVOICE] },
  { label: 'Damage Invoice History', value: 'damage_invoice', eventTypes: [OrderEvent.DAMAGE_INVOICE] }
];
function filterOrderTrail<T extends { eventType: OrderEvent }>(orderTrail: T[], filterValue: string): T[] {
  const option = TIMELINE_FILTER_OPTIONS.find(o => o.value === filterValue);
  if (!option || !option.eventTypes.length) return orderTrail;
  return orderTrail.filter(event => option.eventTypes.includes(event.eventType));
}
function getAvailableTimelineFilterOptions<T extends { eventType: OrderEvent }>(orderTrail: T[]) {
  return TIMELINE_FILTER_OPTIONS.filter(option =>
    !option.eventTypes.length || orderTrail.some(event => option.eventTypes.includes(event.eventType))
  );
}
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  partial: 'Partial Payment',
  security_deposit: 'Security Deposit',
};

function formatPaymentTypes(raw: string): string {
  return raw.split(',').map(t => PAYMENT_TYPE_LABELS[t.trim()] || t.trim()).join(' + ');
}

function isCommentRedundantWithPaymentTypes(event: { comment?: string; customFields?: { paymentTypes?: string } }): boolean {
  return !!event.customFields?.paymentTypes && event.comment === formatPaymentTypes(event.customFields.paymentTypes);
}

interface BikePriceBreakdownTableEntry extends OrderBikePriceBreakdown {
  id: string;
  count: number;
}
interface AccPriceBreakdownTableEntry {
  id: string;
  name: string;
  package: AccessoryPricePackages;
  unitName: PricePackageUnits;
  unitPrice: number;
  unitQty: number;
  quantity: number;
  amount: number;
  surged?: boolean;
}
const separatorSx: SxProps = { width: "10px", height: { xs: "10px", md: "20px" } };

// Tabpanel for swipeable content
interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      style={{ width: 'calc(100% - 4px)' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}
function a11yProps(index: number) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

// Individual booking details
export function BookingDetails(props: {
  order: OrderModel;
  drawerWidth: number;
  backFn: (reload: boolean, orderId?: number, toastState?: { open: boolean; message?: string }) => void;
  setToastState: (params: { open: boolean; message?: string }) => void;
}) {
  // Local state
  const [localState, setLocalState] = useState<{ refreshingDocs?: boolean, sendingLink?: boolean, requestingReview?: boolean, message?: string }>({});
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [orderNoteState, setOrderNoteState] = useState<{ adding?: boolean, messageValue?: string, loading?: boolean, error?: string }>({});
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [timelineFilter, setTimelineFilter] = useState<string>('all');
  const [invoiceDownloadingNumber, setInvoiceDownloadingNumber] = useState<number | null>(null);
  const [damageDownloadingTime, setDamageDownloadingTime] = useState<number | null>(null);

  // Globals and constants
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("md"));
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);

  // Parse props to create local variables
  const dlVerified: DocVerificationStatus = props.order.user?.reputeAttributes?.dl?.verified
    ? "Verified"
    : props.order.user?.reputeAttributes?.dl
      ? "Unverified"
      : "Pending";
  const aadharVerified: DocVerificationStatus = props.order.user?.reputeAttributes?.aadhar?.verified
    ? "Verified"
    : props.order.user?.reputeAttributes?.aadhar
      ? "Unverified"
      : "Pending";
  const vehicleLabel = props.order.bikes?.[0]?.vehicleType === VehicleType.CAR ? 'Car' : 'Bike';
  // Extensions each append their own priceBreakdown entries onto the order (buildExtensionApplyFields), so
  // the same package (e.g. "Weekly package, 7 Days * ₹357.14 * 1 Bike") can appear as several separate
  // entries - club identical ones together and show a count multiplier instead of repeating the row.
  const orderBreakDown: BikePriceBreakdownTableEntry[] = Object.values(
    (props.order.staticData?.priceBreakdown || []).reduce((acc, item, i) => {
      const key = `${item.package}-${item.unitName}-${item.unitPrice}-${item.unitQty}-${item.quantity}`;
      if (!acc[key]) acc[key] = { ...item, id: `bike-${i}`, count: 0, amount: 0 };
      acc[key].count += 1;
      acc[key].amount += item.amount;
      return acc;
    }, {} as { [key: string]: BikePriceBreakdownTableEntry })
  );
  const accBreakDown: AccPriceBreakdownTableEntry[] = [
    ...(props.order.accessoryPriceDetails?.map((a, i) => ({
      id: `acc-${i}`,
      name: a.name,
      package: a.package, // a.accType === AccessoryType.DAILY ? PricePackages.DAILY : PricePackages.TRIP,
      unitName: a.unitName,// a.accType === AccessoryType.DAILY ? PricePackageUnits.DAY_UNIT : PricePackageUnits.TRIP_UNIT,
      unitPrice: a.unitPrice,
      unitQty: a.unitQty, // a.accType === AccessoryType.DAILY ? a.days : 1,
      quantity: a.quantity || NaN,
      amount: a.totalPrice,
    })) || []),
  ];
  const showStartBooking = props.order.rideStatus === RideStatus.NOT_STARTED && (props.order.bookingStatus === BookingStatus.CONFIRMED || props.order.bookingStatus === BookingStatus.EXTENDED);
  const showEndBooking = props.order.rideStatus === RideStatus.STARTED && (props.order.bookingStatus === BookingStatus.CONFIRMED || props.order.bookingStatus === BookingStatus.EXTENDED);
  const showExtendBooking = (props.order.bookingStatus === BookingStatus.CONFIRMED || props.order.bookingStatus === BookingStatus.EXTENDED) && props.order.rideStatus !== RideStatus.ENDED;
  const extensionAvailable = showExtendBooking;
  const showCustomPaymentLink = (props.order.bookingStatus === BookingStatus.CONFIRMED || props.order.bookingStatus === BookingStatus.EXTENDED) && props.order.rideStatus !== RideStatus.ENDED;
  const showRecordCashPayment = showCustomPaymentLink;
  const showDamageInvoice = showCustomPaymentLink;
  const showCancelBooking = showStartBooking && Number(props.order.rideStartScheduled) <= Date.now();
  const showBottomPanel = showExtendBooking || showStartBooking || showEndBooking || showCustomPaymentLink;
  const isSubscriptionOrder = props.order.bookingType === BookingType.SUBSCRIPTION && Boolean(props.order.billingCycle);

  const cancellationDeduction =
    Math.max(props.order.refundAmounts?.vendorRefundSplitAmount || 0, 0) +
    Math.max(props.order.refundAmounts?.vendorDebitAmount || 0, 0) +
    props.order.offlineCashAmount;
  const finalPayableAmount = props.order.finalVendorAmount - cancellationDeduction;
  const ratingToNameMap: { [k in keyof Partial<ReviewModel>]: string } = {
    overallRating: "Overall Rating",
    valueForMoney: "Value for money",
    cleanliness: "Vehicle condition",
    rentalDeskService: "Dealer behaviour",
    pickupProcess: "Pickup process",
    dropProcess: "Drop process",
  };

  // Return DOM
  return (
    <Fragment>

      {/* Booking details desktop view */}
      <div className="bookingDetailsDesktopView">
        <Box className="gbk-tabs gbk-tab-with-header" sx={{ typography: "body1", m: "30px 0 0 0" }}>
          <div className={`container ${isSubscriptionOrder ? 'subscriptionLayout' : ''}`}>
            <div className="leftColumn">

            {/* Customer details - Desktop */}
            <div className="customerDetails">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h2>Customer Details</h2>
                {props.order.user?.reputeAttributes?.latestPic?.doc && (
                  <Button size="small" variant="outlined" onClick={viewProfilePic}>View Profile Pic</Button>
                )}
              </div>

              {/* Profile header card */}
              <div className="bd-profile-card" style={{ marginBottom: 22 }}>
                <div className="bd-avatar">
                  {(props.order.user?.firstName?.[0] || '?').toUpperCase()}{(props.order.user?.lastName?.[0] || '').toUpperCase()}
                </div>
                <div className="bd-profile-text">
                  <div className="bd-profile-name">{props.order.user?.firstName} {props.order.user?.lastName}</div>
                  <div className="bd-profile-sub">{props.order.user?.email || '—'}</div>
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#5a8f1a', lineHeight: 1 }}>{Math.max(0, (props.order.user?.confirmedOrders || 0) - 1)}</div>
                  <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Previous Orders</div>
                </div>
              </div>

              {/* Contact */}
              <div className="bd-section" style={{ marginBottom: 22 }}>
                <div className="bd-section-header" style={{ padding: '0 0 8px' }}>Contact</div>
                <div className="bd-info-list">
                  <div className="bd-info-row">
                    <Call className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Phone</div>
                      <div className="bd-info-value">
                        {props.order.user?.phone}
                        {props.order.user && props.order.user.otherPhone ? `, ${props.order.user.otherPhone}` : ""}
                        {props.order.emPhone ? `, ${props.order.emPhone}` : ""}
                      </div>
                    </div>
                    <a href={`tel:${props.order.user?.phone}`} className="bd-pill-btn">Call</a>
                  </div>
                  <div className="bd-info-row">
                    <LocationOn className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Customer&apos;s Location at the Time of Booking</div>
                      <div className="bd-info-value">
                        {props.order.bookingLocation ? (
                          props.order.bookingLocation.label
                        ) : (
                          <span className="grey-text-light italic">not available</span>
                        )}
                      </div>
                    </div>
                    {props.order.bookingLocation && (
                      <a
                        href={`https://www.google.com/maps?q=${props.order.bookingLocation.lat},${props.order.bookingLocation.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bd-pill-btn"
                      >Map</a>
                    )}
                  </div>
                  <div className="bd-info-row">
                    <Home className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Local Address</div>
                      <div className="bd-info-value">
                        {props.order.localAddress ? (
                          props.order.localAddress
                        ) : (
                          <span className="grey-text-light italic">available after ride start</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Relationship with You */}
              <div className="bd-section" style={{ marginBottom: 22 }}>
                <div className="bd-section-header" style={{ padding: '0 0 8px' }}>Relationship with You</div>
                <div className="bd-info-list">
                  <div className="bd-info-row">
                    <CurrencyRupee className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Lifetime Revenue</div>
                      <div className="bd-info-value">₹{(props.order.user?.vendorStats?.lifetimeRevenue || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="bd-info-row">
                    <DateRange className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Total Rental Days</div>
                      <div className="bd-info-value">{props.order.user?.vendorStats?.totalRentalDays || 0}</div>
                    </div>
                  </div>
                  <div className="bd-info-row">
                    <TaskAlt className="bd-info-icon" />
                    <div className="bd-info-text">
                      <div className="bd-info-label">Total Confirmed Orders</div>
                      <div className="bd-info-value">{props.order.user?.vendorStats?.totalConfirmedOrders || 0}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail">
                <p>ID Proof</p>
                <div className="idProof">
                  <div className="idItem">
                    <p>
                      Driving License{" "}
                      {props.order.user?.reputeAttributes?.dl?.verified ? (
                        <img alt="verified" src={VerifiedIcon} width="16" />
                      ) : (
                        <span
                          className="gbk-icon-span"
                          style={{ color: verificationToColor[dlVerified], paddingLeft: 10 }}
                        >
                          {verificationToIcon[dlVerified]}
                          {dlVerified}
                        </span>
                      )}
                    </p>
                    {props.order.user?.reputeAttributes?.dl?.doc?.db_image && (
                      <button className="bookingBtn" onClick={() => downloadFile("dl/db_image", "dl_db_image.jpg")}>
                        Photo <img alt="download DL avatar" src={DownloadIcon} width="18" />
                      </button>
                    )}
                    {props.order.user?.reputeAttributes?.dl?.doc?.dl_pdf && (
                      <button className="bookingBtn" onClick={() => downloadFile("dl/dl_pdf", "DRVLC.pdf")}>
                        DL PDF <img alt="download DL pdf" src={DownloadIcon} width="18" />
                      </button>
                    )}
                    {props.order.user?.reputeAttributes?.dl?.doc?.dl_front && (
                      <button className="bookingBtn" onClick={() => downloadFile("dl/dl_front", "DRVLC_thumbnail.jpg")}>
                        DL Image <img alt="download DL front" src={DownloadIcon} width="18" />
                      </button>
                    )}
                  </div>
                  <div className="idItem">
                    <p>
                      Aadhaar Card Image{" "}
                      {props.order.user?.reputeAttributes?.aadhar?.verified ? (
                        <img alt="Aadhar verified" src={VerifiedIcon} width="16" />
                      ) : (
                        <span
                          className="gbk-icon-span"
                          style={{ color: verificationToColor[aadharVerified], paddingLeft: 10 }}
                        >
                          {verificationToIcon[aadharVerified]}
                          {aadharVerified}
                        </span>
                      )}
                    </p>
                    {props.order.user?.reputeAttributes?.aadhar?.doc?.db_image && (
                      <button
                        className="bookingBtn"
                        onClick={() => downloadFile("aadhar/db_image", "aadhaar_image.jpg")}
                      >
                        Photo <img alt="download aadhar avatar" src={DownloadIcon} width="18" />
                      </button>
                    )}
                    {props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_pdf && (
                      <button className="bookingBtn" onClick={() => downloadFile("aadhar/aadhaar_pdf", "ADHAR.pdf")}>
                        Aadhaar PDF <img alt="download aadhar pdf" src={DownloadIcon} width="18" />
                      </button>
                    )}
                    {props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_front && (
                      <button
                        className="bookingBtn"
                        onClick={() => downloadFile("aadhar/aadhaar_front", "ADHAR_thumbnail.jpg")}
                      >
                        Aadhaar Image <img alt="download aadhar thumbnail" src={DownloadIcon} width="18" />
                      </button>
                    )}
                  </div>
                </div>
                {/* Refresh repute docs */}
                {
                  (dlVerified !== 'Verified' || aadharVerified !== 'Verified')
                    ? (
                      <div className="gbk-field" style={{ marginTop: 20, width: '100%' }}>
                        <LoadingButton
                          startIcon={<Refresh />}
                          variant={'outlined'}
                          className="thick-border"
                          loading={Boolean(localState.refreshingDocs)}
                          onClick={async () => {
                            setLocalState({ refreshingDocs: true });
                            try {
                              await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/refresh`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                              props.backFn(true, props.order.id);
                            } catch (e: any) {
                              const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                              setLocalState({ message: errorMessage })
                            }
                          }}
                        >
                          Refresh documents
                        </LoadingButton>
                      </div>
                    ) : ''
                }
                {/* Send verification link button */}
                {
                  (dlVerified !== 'Verified' || aadharVerified !== 'Verified')
                    ? (
                      <div className="gbk-field" style={{ marginTop: 20, width: '100%' }}>
                        {/* Send link button */}
                        <LoadingButton
                          startIcon={<Refresh />}
                          variant={'outlined'}
                          className="thick-border"
                          loading={Boolean(localState.sendingLink)}
                          onClick={async () => {
                            setLocalState({ sendingLink: true });
                            try {
                              await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/resend`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                              props.backFn(true, props.order.id, { open: true, message: 'SMS sent to customer!' });
                            } catch (e: any) {
                              const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                              setLocalState({ message: errorMessage })
                            }
                          }}
                        >
                          Send Verification Link
                        </LoadingButton>
                        {/* Copy link */}
                        {
                          props.order.user?.reputeUrl ? (
                            <Button
                              onClick={() => {
                                navigator.clipboard.writeText(props.order.user?.reputeUrl || '');
                                props.setToastState({ open: true, message: 'Verification link copied!' })
                              }}
                            >
                              <ContentCopyRounded />
                            </Button>
                          ) : ''
                        }
                      </div>
                    )
                    : ''
                }
                {/* Error message */}
                <div className="gbk-field" style={{ marginTop: 20, color: Colors.failureRed }}>
                  {localState.message}
                </div>
              </div>
            </div>

            {/* Pickup dropoff details - Desktop */}
            <div className="pickupDropoffDetails">
              <h2>Pickup and Drop off Details</h2>
              <div className="flex-row">
                <div className="detail">
                  <p>Pickup Date And Time</p>
                  <p>
                    {DateTime.fromMillis(Number(props.order.rideStartScheduled)).toLocaleString(DateTime.DATETIME_MED)}
                  </p>
                </div>
                <div className="detail">
                  <p>Actual Pickup Date And Time</p>
                  <p>
                    {props.order.rideStartActual ? DateTime.fromMillis(Number(props.order.rideStartActual)).toLocaleString(DateTime.DATETIME_MED) : '-'}
                  </p>
                </div>
              </div>
              <div className="flex-row">
                <div className="detail">
                  <p>Drop Off Date And Time</p>
                  <p>
                    {DateTime.fromMillis(Number(props.order.rideEndScheduled)).toLocaleString(DateTime.DATETIME_MED)}
                  </p>
                </div>
                <div className="detail">
                  <p>Actual Drop Off Date And Time</p>
                  <p>
                    {props.order.rideEndActual ? DateTime.fromMillis(Number(props.order.rideEndActual)).toLocaleString(DateTime.DATETIME_MED) : '-'}
                  </p>
                </div>
              </div>
              <div className="flex-row">
                <div className="detail">
                  <p>Odometer Start Reading</p>
                  <p>{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odStart : "-"}</p>
                </div>
                <div className="detail">
                  <p>Odometer End Reading</p>
                  <p>{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odEnd || "-" : "-"}</p>
                </div>
              </div>
              <div className="flex-row">
                <div className="detail">
                  <p>Distance Limit</p>
                  <p>{props.order.staticData?.distanceLimit && props.order.staticData.distanceLimit > 0 ? `${props.order.staticData.distanceLimit} Km` : 'No limit'}</p>
                </div>
                <div className="detail">
                  <p>Extra KM Charge</p>
                  <p>₹ {props.order.staticData?.penalty?.distance || 0} / Km</p>
                </div>
              </div>
              <div className="flex-row">
                <div className="detail">
                  <p>Booking Status</p>
                  <p
                    className="StatusBtn bgGreen"
                    style={{ background: bookingStatusToColor[props.order.bookingStatus] }}
                  >
                    {" "}
                    {props.order.bookingStatus.charAt(0) + props.order.bookingStatus.slice(1).toLowerCase()}
                  </p>
                </div>
                <div className="detail">
                  <p>Ride Status</p>
                  <p className="StatusBtn bgYellow" style={{ background: rideStatusToColor[props.order.rideStatus] }}>
                    {" "}
                    {RideStatusToText[props.order.rideStatus]}
                  </p>
                </div>
              </div>
              {/* Delivery details */}
              {
                (props.order.deliveryDetails && props.order.deliveryDetails.required) ? (
                  <div className="detail">
                    <p style={{ marginTop: '10px' }}>Delivery Details</p>
                    <p>Delivery required: <b>Yes</b></p>
                    <p style={{ margin: 0 }}>Delivery address: <b>{props.order.deliveryDetails.address || 'No address provided'}</b></p>
                    <p style={{ margin: 0 }}>Delivery charges: <b>{props.order.deliveryAmount ? `₹ ${props.order.deliveryAmount}` : '-'}</b></p>
                    <p style={{ margin: 0 }}>
                      Delivery location:{' '}
                      {props.order.deliveryDetails.address ? (
                        <a
                          href={`https://www.google.com/maps?q=${encodeURIComponent(props.order.deliveryDetails.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on map
                        </a>
                      ) : (
                        <span className="grey-text-light italic">not available</span>
                      )}
                    </p>
                  </div>
                ) : ''
              }
            </div>

            {/* Booking details - Desktop */}
            <div className="bookingDetails">
              <h2>Booking Details</h2>
              <div className="detail" style={{ float: 'left' }}>
                <p>{`${props.order?.bikes?.[0].brand} ${props.order?.bikes?.[0].name}`}{props.order?.bikes?.[0].vehicleType === 'CAR' ? ` (${props.order.bikes[0].startingMethod === 'AUTO' ? 'Automatic' : props.order.bikes[0].startingMethod}, ${props.order.bikes[0].engineType?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())})` : ''}</p>
                {props.order.bikeDetails?.some(b => b.batteryNumber) && (
                  <p style={{ fontSize: '14px', marginTop: '2px' }}>
                    Battery: {props.order.bikeDetails.map(b => b.batteryNumber).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              <div
                style={{ float: 'right', marginTop: '-6px' }}
                hidden={props.order.rideStatus === RideStatus.ENDED || ![BookingStatus.CONFIRMED, BookingStatus.EXTENDED].includes(props.order.bookingStatus)}
              >
                <EditBooking style={{ marginLeft: 10 }} order={props.order} hub={selectedHub} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    startIcon={<Edit />}
                    variant={'contained'}
                    className="thick-border no-text-transform"
                    size="small"
                  >
                    Change vehicle
                  </Button>
                </EditBooking>
              </div>
              <DataGrid
                style={{ clear: 'both' }}
                disableColumnMenu={true}
                disableColumnFilter={true}
                density="compact"
                className="gbk-dg phone"
                rowHeight={120}
                hideFooter={true}
                headerHeight={0}
                autoHeight={true}
                rows={orderBreakDown}
                columns={[
                  {
                    field: "package",
                    width: 200,
                    align: "center",
                    renderCell: (o: { row: BikePriceBreakdownTableEntry }) => `${packageNameToString[o.row.package]}`,
                  },
                  {
                    field: "breakdown",
                    width: 200,
                    align: "center",
                    renderCell: (o: { row: BikePriceBreakdownTableEntry }) =>
                      `${o.row.count > 1 ? `${o.row.count} x ` : ""}${o.row.unitQty} ${unitNameToString[o.row.unitName]}${o.row.unitQty > 1 ? "s" : ""} * ₹ ${o.row.unitPrice
                      } * ${o.row.quantity} ${vehicleLabel}${o.row.quantity > 1 ? "s" : ""}`,
                  },
                  {
                    field: "amount",
                    width: 200,
                    align: "center",
                    renderCell: (o: { row: BikePriceBreakdownTableEntry }) => (
                      <span style={{ fontWeight: "bold" }}>{`₹ ${o.row.amount.toFixed(2)}`}</span>
                    ),
                  },
                ]}
                components={{
                  Row: (data: { row: BikePriceBreakdownTableEntry }) => (
                    <div className="dailyPackage">
                      <span>{`${packageNameToString[data.row.package]}`}</span>
                      <br />
                      <span style={{ fontWeight: "bold", paddingLeft: "20px" }}>
                        {`${data.row.count > 1 ? `${data.row.count} x ` : ""}${data.row.unitQty} ${unitNameToString[data.row.unitName]}${data.row.unitQty > 1 ? "s" : ""
                          } * ₹ ${data.row.unitPrice} * ${data.row.quantity} ${vehicleLabel}${data.row.quantity > 1 ? "s" : ""}`}
                      </span>
                      <span style={{ fontWeight: "bold", color: Colors.primary }}>{`₹ ${data.row.amount.toFixed(
                        2
                      )}`}</span>
                    </div>
                  ),
                }}
                pageSize={100}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
              />
              {accBreakDown.map((a, i) => (
                <Fragment key={i}>
                  <Typography>
                    <b>{a.name}</b>
                  </Typography>
                  <div style={{ height: 65 + 1 * 76 }}>
                    <DataGrid
                      style={{ clear: 'both' }}
                      disableColumnMenu={true}
                      disableColumnFilter={true}
                      density='compact'
                      className='gbk-dg phone'
                      rowHeight={120}
                      hideFooter={true}
                      headerHeight={0}
                      autoHeight={true}
                      rows={[a]}
                      columns={[{
                        field: 'package',
                        width: 200,
                        align: 'center',
                        renderCell: (o: { row: AccPriceBreakdownTableEntry }) => `${packageNameToString[o.row.package]}`
                      }, {
                        field: 'breakdown',
                        width: 200,
                        align: 'center',
                        renderCell: (o: { row: AccPriceBreakdownTableEntry }) => `${o.row.unitQty} ${unitNameToString[o.row.unitName]}${o.row.unitQty > 1 ? 's' : ''} * ₹ ${o.row.unitPrice} * ${o.row.quantity} Unit${o.row.quantity > 1 ? 's' : ''}`
                      }, {
                        field: 'amount',
                        width: 200,
                        align: 'center',
                        renderCell: (o: { row: AccPriceBreakdownTableEntry }) => <span style={{ fontWeight: 'bold' }} >{`₹ ${o.row.amount}`}</span>
                      }]}
                      components={{
                        Row: (data: { row: AccPriceBreakdownTableEntry }) => (
                          <div className="dailyPackage">
                            <span>{`${packageNameToString[data.row.package]}`}</span>
                            <span style={{ fontWeight: 'bold', paddingLeft: '20px' }} >
                              {`${data.row.unitQty} ${unitNameToString[data.row.unitName]}${data.row.unitQty > 1 ? 's' : ''} * ₹ ${data.row.unitPrice} * ${data.row.quantity} ${vehicleLabel}${data.row.quantity > 1 ? 's' : ''}`}
                            </span>
                            <span style={{ fontWeight: 'bold', color: Colors.primary }} >{`₹ ${data.row.amount.toFixed(2)}`}</span>
                          </div>
                        )
                      }}
                      pageSize={100}
                      rowsPerPageOptions={[5]}
                      disableSelectionOnClick
                    />
                  </div>
                </Fragment>
              ))}
              <div className="flex-row">
                <div className="detail">
                  <p>Booking Date And Time</p>
                  <p>{DateTime.fromISO(props.order.createdAt).toLocaleString(DateTime.DATETIME_MED)}</p>
                </div>
                <div className="detail">
                  <p>Vehicle Numbers</p>
                  <p>
                    {" "}
                    {props.order.bikeDetails ? (
                      props.order.bikeDetails?.map((b) => b.rcNumber).join(",")
                    ) : (
                      <span className="grey-text-light italic" style={{ fontSize: "16px" }}>
                        Available after ride start
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex-row">
                <div className="detail">
                  <p >Order Notes</p>
                  <label className="value">
                    {props.order.orderNotes && props.order.orderNotes.length ? (
                      <ul className='gbk-list no-margin'>
                        {props.order.orderNotes.map(on => (
                          <li className="fs-14">{on.message}</li>
                        ))}
                      </ul>
                    ) : ''}
                    <Box className="gbk-col no-pad-bot" sx={{ width: { xs: '150%', md: 600 }, display: orderNoteState.adding ? 'block' : 'none' }}>
                      <GobikesField
                        type={GbkFieldType.STRING}
                        editMode={true}
                        value={orderNoteState.messageValue || ''}
                        updateFn={(v: string) => {
                          setOrderNoteState({ ...orderNoteState, messageValue: v })
                        }}
                      />
                    </Box>
                    <div className="error-text" style={{ padding: '10px 0 5px 0' }}>
                      {orderNoteState.error}
                    </div>
                    <div>
                      <Button
                        style={{ ...(orderNoteState.adding && { display: 'none' }) }}
                        key={'add'}
                        size="small"
                        variant="contained"
                        onClick={() => { setOrderNoteState({ adding: true }) }}
                      >
                        Add note
                      </Button>
                      <Button
                        style={{ ...(!orderNoteState.adding && { display: 'none' }) }}
                        key={'save'}
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          setOrderNoteState({ loading: true });
                          try {
                            await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/notes`, { message: orderNoteState.messageValue }, { headers: { 'vendor-token': authCookie } });
                            props.backFn(true, props.order.id);
                          } catch (e: any) {
                            const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                            setOrderNoteState({ ...orderNoteState, error: errorMessage })
                          }
                        }}
                      >
                        Save
                      </Button> &nbsp;&nbsp;
                      <Button
                        style={{ ...(!orderNoteState.adding && { display: 'none' }) }}
                        key={'cancel'}
                        size="small"
                        variant="outlined"
                        onClick={() => { setOrderNoteState({ adding: false }) }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </label>
                </div>
              </div>
              {/* Timeline */}
              <div className="flex-row">
                <div className="detail">
                  <br />
                  {renderTimeline()}
                </div>
              </div>
            </div>

            </div>
            <div className="rightColumn">

            {/* Payment details - Desktop */}
            <div className="paymentDetails">
              <h2>
                Payment Details{" "}
                <button className="bookingBtn" style={{ cursor: "text" }}>
                  {props?.order?.paymentMode}
                </button>
              </h2>
              <div className="detailRow">
                <p>Vehicle Rental</p>
                <p>₹ {props.order.totalBikeAmount}</p>
              </div>
              <div className="detailRow" style={{ ...(props.order.totalAccessoryAmount < Number.EPSILON && { display: 'none' }) }}>
                <p>Accessory Rental</p>
                <p>₹ {props.order.totalAccessoryAmount}</p>
              </div>
              {(() => {
                const kmAmount = props.order.kmPackagePriceDetails?.reduce((s, d) => s + d.charge, 0) ?? 0;
                return kmAmount > Number.EPSILON ? (
                  <div className="detailRow">
                    <p>Extra KM Charges</p>
                    <p>₹ {kmAmount.toFixed(2)}</p>
                  </div>
                ) : null;
              })()}
              <div className="detailRow" style={{ ...(props.order.defaultDiscount < Number.EPSILON && { display: 'none' }) }}>
                <p>Default Discount Offered By Dealer</p>
                <p className="colorGreen">- ₹ {props.order.defaultDiscount}</p>
              </div>
              <div className="detailRow" style={{ ...(props.order.vendorDiscount - props.order.defaultDiscount < Number.EPSILON && { display: 'none' }) }}>
                <p>Coupon Discount Offered By Dealer</p>
                <p className="colorGreen">- ₹ {props.order.vendorDiscount - props.order.defaultDiscount}</p>
              </div>
              <div className="detailRow">
                <p>
                  <strong>Total Amount Considered For Settlement (A)</strong>
                </p>
                <p>
                  <strong>
                    ₹ {props.order.totalBikeAmount + props.order.totalAccessoryAmount - props.order.vendorDiscount}
                  </strong>
                </p>
              </div>
              <div className="detailRow" style={{ ...(props.order.gobikesDiscount < Number.EPSILON && { display: 'none' }) }}>
                <p>Coupon Discount Offered By GoWheelo</p>
                <p className="colorGreen">- ₹ {props.order.gobikesDiscount}</p>
              </div>
              <div className="detailRow" style={{ ...(props.order.goCoinsUsed < Number.EPSILON && { display: 'none' }) }}>
                <p>Gocoins Discount Offered By GoWheelo</p>
                <p className="colorGreen">- ₹ {props.order.goCoinsUsed}</p>
              </div>
              <hr />
              <div className="detailRow">
                <p>
                  <strong>
                    {props.order.goBikesCommission > Number.EPSILON ? 'GoWheelo Commission (including GST and Payment Gateway Charges)' : 'Payment Gateway Charges'} (B)
                  </strong>
                </p>
                <p style={{ minWidth: '100px', textAlign: 'right' }}>₹ {(props.order.goBikesCommission + props.order.tdrAmount + props.order.convFees).toFixed(2)}</p>
              </div>
              <div className="detailRow" style={{ ...(props.order.gobikesDiscount + props.order.goCoinsUsed < Number.EPSILON && { display: 'none' }) }}>
                <p>
                  Final GoWheelo Commission <br />
                  (Commission - Discount offered by GoWheelo)
                </p>
                <p className="colorGreen">
                  ₹{" "}
                  {(
                    props.order.goBikesCommission + props.order.tdrAmount + props.order.convFees
                    - props.order.gobikesDiscount - props.order.goCoinsUsed
                  ).toFixed(2)}
                </p>
              </div>
              <hr />
              <div
                style={{ display: !(props.order.paymentMode !== PaymentMode.OFFLINE) ? "none" : "" }}
                className="detailRow"
              >
                <p className="float-left">Amount{props.order.paymentDone ? " " : " to be "}paid online by customer</p>
                <p className="float-right roboto">₹ {props.order.amountPayableOnline.toFixed(2)}</p>
              </div>
              <div
                style={{ display: !(props.order.paymentMode !== PaymentMode.ONLINE) ? "none" : "" }}
                className="detailRow"
              >
                <p className="float-left bold">Cash to be collected OFFLINE</p>
                <p className="float-right roboto bold">₹ {props.order.offlineCashAmount.toFixed(2)}</p>
              </div>
              <div
                style={{ display: !(props.order.paymentMode !== PaymentMode.OFFLINE) ? "none" : "" }}
                className="detailRow"
              >
                <p className={`float-left ${props.order.bookingStatus === BookingStatus.CANCELLED ? "" : "bold"}`}>
                  {props.order.bookingStatus === BookingStatus.CANCELLED ? "" : "Final "}Payable Amount (A-B)
                </p>
                <p
                  className={`float-right roboto ${props.order.bookingStatus === BookingStatus.CANCELLED ? "" : "bold"
                    }`}
                >
                  ₹ {props.order.finalVendorAmount.toFixed(2)}
                </p>
              </div>
              <div
                className="detailRow"
                style={{
                  display: !(
                    props.order.bookingStatus === BookingStatus.CANCELLED &&
                    props.order.refundAmounts &&
                    (props.order.refundAmounts.vendorDebitAmount || props.order.refundAmounts.vendorRefundSplitAmount)
                  )
                    ? "none"
                    : "",
                }}
              >
                <p className="float-left">Cancellation deduction</p>
                <p className="float-right roboto">- ₹ {cancellationDeduction.toFixed(2)}</p>
              </div>
              <div
                className="detailRow"
                style={{
                  display: !(
                    props.order.bookingStatus === BookingStatus.CANCELLED &&
                    props.order.refundAmounts &&
                    (props.order.refundAmounts.vendorDebitAmount || props.order.refundAmounts.vendorRefundSplitAmount)
                  )
                    ? "none"
                    : "",
                }}
              >
                <p className="float-left bold">Final Payable Amount</p>
                <p className="float-right roboto bold"> ₹ {finalPayableAmount.toFixed(2)}</p>
              </div>
              <div className="detailRow" style={{ ...(props.order.depositAmount < Number.EPSILON && { display: 'none' }) }}>
                <p>Security Deposit</p>
                <p>₹ {props.order.depositAmount}</p>
              </div>
            </div>

            {/* Customer review - Desktop */}
            {props.order.orderMode !== OrderMode.VENDOR && (
              <div className="customerReview">
                <h2>
                  Customer Review{" "}
                  <button
                    className="bookingBtn"
                    disabled={Boolean(localState.requestingReview)}
                    onClick={async () => {
                      setLocalState({ requestingReview: true });
                      try {
                        await GobikesApi.post(
                          `/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/request-review`,
                          { userId: props.order.userId },
                          { headers: { "vendor-token": authCookie } }
                        );
                        props.backFn(true, props.order.id, { open: true, message: "SMS and mail sent to customer!" });
                      } catch (e: any) {
                        const errorMessage =
                          e.response && e.response.data && e.response.data.message
                            ? "Error: " + e.response.data.message
                            : "An error occured";
                        setLocalState({ message: errorMessage });
                      }
                    }}
                  >
                    Ask for review again
                  </button>
                </h2>
                {props.order.reviews && props.order.reviews[0] ? (
                  <div>
                    {Object.keys(ratingToNameMap).map((f) => (
                      <div key={f} className="flex-row">
                        <p>{ratingToNameMap[f as keyof ReviewModel]}</p>
                        <div className="stars">
                          {Array.from(
                            Array(Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()
                          ).map((_, i) => (
                            <span key={i} className="gbk-icon-span large">
                              <Star className="rating-star" />
                            </span>
                          ))}
                          {Array.from(
                            Array(5 - Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()
                          ).map((_, i) => (
                            <span key={i} className="gbk-icon-span large">
                              <Star className="grey-text-medium" />
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="detail">
                      <p>Comments</p>
                      <p> {props.order.reviews?.[0].message || "No message added"}</p>
                    </div>
                  </div>
                ) : (
                  <label className="label">No review available</label>
                )}
              </div>
            )}

            {/* Invoice history - Desktop (subscription orders only) */}
            {isSubscriptionOrder && (
              <div className="invoiceHistory">
                <h2>Invoice History</h2>
                {renderInvoiceHistoryRows()}
              </div>
            )}

            </div>
          </div>
        </Box >

        {/* Action buttons */}
        < Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: { xs: 0, md: props.drawerWidth },
            width: { xs: "100vw", md: `calc(100vw - ${props.drawerWidth}px)` },
            padding: { xs: "20px 0", md: "20px 40px" },
            background: "#fff",
            boxShadow: "0px -2px 6px #ddd",
            ...(!showBottomPanel && { display: "none" }),
          }
          }
        >
          {/* {
          props.order.rideStatus === RideStatus.NOT_STARTED && props.order.bookingStatus === BookingStatus.CONFIRMED
            ? (
              <CancelBooking order={props.order}>
                <Button className="thick-border" variant="outlined">Cancel Booking</Button>
              </CancelBooking>
            ) : ''
        } */}
          {
            showExtendBooking ? (
              extensionAvailable ? (
                <ExtendBooking
                  style={{ marginLeft: 10 }}
                  order={props.order}
                  backFn={() => props.backFn(true, props.order.id)}
                >
                  <Button size={isPhone ? 'small' : 'medium'} className="thick-border no-text-transform action-btn" variant="outlined">
                    Extend Booking
                  </Button>
                </ExtendBooking>
              ) : (
                <Button size={isPhone ? 'small' : 'medium'} className="thick-border no-text-transform action-btn" variant="outlined" disabled={true}>
                  No extension available
                </Button>
              )
            ) : (
              ""
            )
          }
          {
            showStartBooking ? (
              <StartBooking
                style={{ marginLeft: 10 }}
                order={props.order}
                hub={selectedHub}
                backFn={() => props.backFn(true, props.order.id)}
              >
                <Button
                  size={isPhone ? 'small' : 'medium'}
                  variant="outlined"
                  sx={{ xs: { width: "calc(100% - 40px)", ml: "10px" } }}
                  className="thick-border no-text-transform action-btn"
                >
                  Start Pickup Process
                </Button>
              </StartBooking>
            ) : (
              ""
            )
          } {" "}
          {
            showEndBooking ? (
              <EndBooking
                style={{ marginLeft: 10 }}
                order={props.order}
                backFn={() => props.backFn(true, props.order.id)}
              >
                <Button
                  size={isPhone ? 'small' : 'medium'}
                  variant="outlined"
                  sx={{ xs: { width: "calc(100% - 40px)", ml: "10px" } }}
                  className="thick-border no-text-transform action-btn"
                >
                  Start Drop-Off Process
                </Button>
              </EndBooking>
            ) : (
              ""
            )
          } {" "}
          {
            showCustomPaymentLink ? (
              <CreateCustomPaymentLink
                style={{ marginLeft: 10 }}
                order={props.order}
                backFn={() => props.backFn(true, props.order.id)}
              >
                <Button className="thick-border no-text-transform action-btn" variant="outlined">
                  Create Payment Link
                </Button>
              </CreateCustomPaymentLink>
            ) : (
              ""
            )
          }
          {
            showRecordCashPayment ? (
              <RecordCashPayment
                style={{ marginLeft: 10 }}
                order={props.order}
                backFn={() => props.backFn(true, props.order.id)}
                isSubscription={isSubscriptionOrder}
              >
                <Button className="thick-border no-text-transform action-btn" variant="outlined">
                  Record Cash Payment
                </Button>
              </RecordCashPayment>
            ) : (
              ""
            )
          }
          {
            showDamageInvoice ? (
              <DamageInvoice
                style={{ marginLeft: 10 }}
                order={props.order}
                backFn={() => props.backFn(true, props.order.id)}
              >
                <Button className="thick-border no-text-transform action-btn" variant="outlined">
                  Damage Invoice
                </Button>
              </DamageInvoice>
            ) : (
              ""
            )
          }
          {
            showCancelBooking ? (
              <CancelBooking
                style={{ marginLeft: 10 }}
                order={props.order}
                backFn={() => props.backFn(true, props.order.id)}
              >
                <Button className="thick-border no-text-transform action-btn" variant="outlined">
                  Cancel Booking
                </Button>
              </CancelBooking>
            ) : (
              ""
            )
          }
        </Box >
        <br />
        <br />
      </div >

      {/* Booking details phone view */}
      <div className="bookingDetailsMobileView">
        <div className="gbk-paper-container booking-details">

          {/* Empty space at top for phone screen - taller when the header shows the subscription billing badge (Outstanding/Next Billing/Cycle), which pushes the fixed header taller */}
          <Fragment>
            <Box sx={{
              display: { xs: 'block', md: 'none' },
              height: (isSubscriptionOrder && props.order.bookingStatus !== BookingStatus.PENDING) ? { xs: '92px', sm: '96px' } : { xs: '48px', sm: '52px' },
              width: 1
            }}><br /></Box>
          </Fragment>

          {/* Tabs */}
          <TabContext value={selectedTab.toString()} >
            <Box sx={{ marginTop: '6px', marginBottom: `${isPhone ? '6' : '20'}px`, position: 'relative' }}>
              {/* Booking status */}
              {isPhone ? '' : (
                <Box sx={{ position: 'absolute', right: '0', bottom: '12px' }}>
                  <div
                    className="roboto"
                    style={{
                      border: `1px solid ${bookingStatusToColor[props.order.bookingStatus]}`,
                      color: bookingStatusToColor[props.order.bookingStatus],
                      background: bookingStatusToBgColor[props.order.bookingStatus],
                      borderRadius: '8px',
                      padding: '6px 10px',
                      width: '100px',
                      textAlign: 'center',
                      display: 'inline-block',
                      fontSize: '16px'
                    }}
                  >
                    {props.order.bookingStatus}
                  </div >
                  <br />
                  {props.order.orderMode === OrderMode.VENDOR ? (
                    <div
                      className="roboto"
                      style={{
                        border: `1px solid #444`,
                        color: '#fff',
                        background: '#444',
                        borderRadius: '8px',
                        padding: '6px 10px',
                        width: '100px',
                        textAlign: 'center',
                        display: 'inline-block',
                        fontSize: '16px',
                        marginTop: '8px'
                      }}
                    >
                      SELF
                    </div >
                  ) : ''}
                </Box>
              )}

              {/* Tab headers */}
              <Tabs
                value={selectedTab}
                variant={isPhone ? 'fullWidth' : 'scrollable'}
                onChange={(_, tab) => setSelectedTab(tab)}
                sx={{
                  ...(isPhone && {
                    backgroundColor: '#e4e6eb',
                    borderRadius: '14px',
                    padding: '4px',
                    minHeight: 'unset',
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTabs-flexContainer': { gap: '4px' },
                  }),
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 0,
                    padding: isPhone ? '6px 4px' : undefined,
                    ...(isPhone && {
                      borderRadius: '10px',
                      backgroundColor: 'transparent',
                      color: '#777',
                      margin: '0',
                      boxShadow: 'none',
                      minHeight: '48px',
                    }),
                  },
                  '& .MuiTab-root.Mui-selected': isPhone ? {
                    backgroundColor: '#fff',
                    color: '#5a8f1a',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  } : {},
                  '& .MuiTab-root .MuiTab-iconWrapper': { marginBottom: isPhone ? '2px' : undefined },
                  '& .MuiTab-root span': { fontSize: isPhone ? '10px' : '16px' },
                }}
              >
                <Tab
                  icon={<AccountCircle fontSize={isPhone ? 'small' : 'medium'} />}
                  className='gbk-tabs-header'
                  label={<span>Customer<br />Details</span>}
                  {...a11yProps(0)}
                />
                <Tab
                  icon={<MenuBook fontSize={isPhone ? 'small' : 'medium'} />}
                  className='gbk-tabs-header'
                  label={<span>Booking<br />Details</span>}
                  {...a11yProps(1)}
                />
                <Tab
                  icon={<CurrencyRupee fontSize={isPhone ? 'small' : 'medium'} />}
                  className='gbk-tabs-header'
                  label={<span>Payment<br />Details</span>}
                  {...a11yProps(2)}
                />
                {isSubscriptionOrder && (
                  <Tab
                    icon={<Download fontSize={isPhone ? 'small' : 'medium'} />}
                    className='gbk-tabs-header'
                    label={<span>Invoice<br />History</span>}
                    {...a11yProps(3)}
                  />
                )}
              </Tabs>
            </Box>

            <SwipeableViews
              axis='x'
              index={selectedTab}
              onChangeIndex={(tab) => setSelectedTab(tab)}
            >

              {/* SECTION - Customer details */}
              <TabPanel value={selectedTab} index={0} dir="x">
                {renderCustomerDetails()}
              </TabPanel>

              {/* SECTION - Booking details */}
              <TabPanel value={selectedTab} index={1} dir="x">
                {renderBookingDetails()}
              </TabPanel>

              {/* SECTION - Payment details */}
              <TabPanel value={selectedTab} index={2} dir="x">
                {renderPaymentDetails()}
              </TabPanel>

              {/* SECTION - Invoice history (subscription orders only) */}
              {isSubscriptionOrder && (
                <TabPanel value={selectedTab} index={3} dir="x">
                  <Paper
                    className="gbk-paper"
                    sx={{
                      width: 'calc(100% - 2px)',
                      minHeight: '100%',
                      boxSizing: 'border-box',
                      padding: '16px',
                      ...(isPhone && {
                        maxHeight: 'calc(100vh - 230px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch'
                      })
                    }}
                    elevation={0}
                  >
                    {renderInvoiceHistoryRows()}
                  </Paper>
                </TabPanel>
              )}

            </SwipeableViews>

          </TabContext>
        </div >

        {/* Action buttons */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: props.drawerWidth },
          width: { xs: '100vw', md: `calc(100vw - ${props.drawerWidth}px)` },
          padding: { xs: '10px 12px', md: '20px 40px' },
          paddingBottom: { xs: 'calc(10px + env(safe-area-inset-bottom, 0px))', md: 'calc(20px + env(safe-area-inset-bottom, 0px))' },
          background: '#fff',
          boxShadow: '0px -2px 6px #ddd',
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '6px',
          alignItems: 'center',
          boxSizing: 'border-box',
          ...(!showBottomPanel && { display: 'none' })
        }}>
          {/* {
          props.order.rideStatus === RideStatus.NOT_STARTED && props.order.bookingStatus === BookingStatus.CONFIRMED
            ? (
              <CancelBooking order={props.order}>
                <Button className="thick-border" variant="outlined">Cancel Booking</Button>
              </CancelBooking>
            ) : ''
        } */}
          {
            showExtendBooking
              ? (
                (extensionAvailable
                  ? (
                    <ExtendBooking style={{ marginLeft: 0 }} order={props.order} backFn={() => props.backFn(true, props.order.id)}>
                      <Button size={isPhone ? 'small' : 'medium'} className="thick-border no-text-transform action-btn" variant="outlined" sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: isPhone ? '11px' : undefined }}>Extend Booking</Button>
                    </ExtendBooking>
                  )
                  : <Button size={isPhone ? 'small' : 'medium'} className="thick-border no-text-transform action-btn" variant="outlined" disabled={true} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>No extension available</Button>
                )
              ) : ''
          }
          {
            showStartBooking
              ? (
                <StartBooking style={{ marginLeft: 0, flex: isPhone ? 1 : undefined }} order={props.order} hub={selectedHub} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    size={isPhone ? 'small' : 'medium'}
                    variant="outlined"
                    sx={{ width: isPhone ? '100%' : undefined, minWidth: 0, fontSize: isPhone ? '11px' : undefined }}
                    className="thick-border no-text-transform action-btn booking-primary-action"
                  >
                    Start Pickup Process
                  </Button>
                </StartBooking>
              ) : ''
          } {
            showEndBooking
              ? (
                <EndBooking style={{ marginLeft: 0, flex: isPhone ? 1 : undefined }} order={props.order} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    size={isPhone ? 'small' : 'medium'}
                    variant="outlined"
                    sx={{ width: isPhone ? '100%' : undefined, minWidth: 0, fontSize: isPhone ? '11px' : undefined }}
                    className="thick-border no-text-transform action-btn"
                  >
                    Start Drop-Off Process
                  </Button>
                </EndBooking>
              ) : ''
          }
          {/* Add Note icon button — phone only */}
          {isPhone && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit fontSize="small" />}
              onClick={() => setOrderNoteState({ adding: true, messageValue: '' })}
              sx={{ height: 36, borderRadius: '10px', borderColor: '#c8e896', color: '#5a8f1a', textTransform: 'none', fontWeight: 600, fontSize: '11px', flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Add Note
            </Button>
          )}
        </Box>
      </div >

      {/* Profile pic preview dialog */}
      <Dialog open={Boolean(profilePicUrl)} onClose={() => { if (profilePicUrl) URL.revokeObjectURL(profilePicUrl); setProfilePicUrl(null); }} maxWidth="sm">
        <DialogContent>
          {profilePicUrl && (
            <img src={profilePicUrl} alt="Customer Profile" style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Phone — bottom drawer for note entry (top-level so it works on all tabs) */}
      {isPhone && (
        <Drawer
          anchor="bottom"
          open={Boolean(orderNoteState.adding)}
          onClose={() => setOrderNoteState({ adding: false })}
          PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 20px 40px' } }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '16px' }}>Add Note</Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Write your note here..."
            value={orderNoteState.messageValue || ''}
            onChange={(e) => setOrderNoteState({ ...orderNoteState, messageValue: e.target.value })}
            sx={{ mb: 1.5 }}
          />
          {orderNoteState.error && (
            <div className="error-text" style={{ padding: '4px 0 8px' }}>{orderNoteState.error}</div>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              variant="contained"
              loading={Boolean(orderNoteState.loading)}
              sx={{ flex: 1, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              onClick={async () => {
                setOrderNoteState({ ...orderNoteState, loading: true });
                try {
                  await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/notes`, { message: orderNoteState.messageValue }, { headers: { 'vendor-token': authCookie } });
                  props.backFn(true, props.order.id);
                } catch (e: any) {
                  setOrderNoteState({ ...orderNoteState, loading: false, error: (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occured' });
                }
              }}
            >
              Save Note
            </LoadingButton>
            <Button
              variant="outlined"
              sx={{ borderRadius: '10px', textTransform: 'none' }}
              onClick={() => setOrderNoteState({ adding: false })}
            >
              Cancel
            </Button>
          </Box>
        </Drawer>
      )}
    </Fragment >
  );

  /*
   * Render utility functions beyond this point
   */

  function getInvoiceHistoryRows() {
    if (!isSubscriptionOrder) return [];

    const firstCycleEnd = computeBillingCycleEnd(Number(props.order.rideStartScheduled), props.order.billingCycle as BillingCycle);
    // Merge cron/manual per-cycle invoices with bulk multi-cycle pre-payments made via Extend Booking - both represent real invoices, just from different flows
    const laterRows = [
      ...(props.order.subscription_invoices || []).map(inv => ({
        cycleStart: Number(inv.cycleStart),
        cycleEnd: Number(inv.cycleEnd),
        amount: inv.amount,
        isPaid: inv.status === SubscriptionInvoiceStatus.PAID,
        paidAt: inv.paidAt,
        paymentLink: inv.paymentLink as string | undefined,
        createdAt: inv.createdAt
      })),
      ...(props.order.order_extensions || []).filter(ext => !ext.voided).map(ext => ({
        cycleStart: Number(ext.extStart),
        cycleEnd: Number(ext.extEnd),
        amount: ext.extraAmount,
        isPaid: ext.paymentDone,
        paidAt: ext.paymentTime,
        paymentLink: ext.paymentLink as string | undefined,
        createdAt: ext.createdAt
      }))
    ].sort((a, b) => a.cycleStart - b.cycleStart);

    const cycleRows = [
      {
        type: 'cycle' as const,
        invoiceNumber: 1,
        cycleStart: Number(props.order.rideStartScheduled),
        cycleEnd: firstCycleEnd,
        amount: props.order.currentTotalPayableAmountWithoutExt,
        isPaid: Boolean(props.order.paymentDone),
        paidAt: props.order.paymentTime,
        paymentLink: undefined as string | undefined,
        createdAt: props.order.createdAt
      },
      ...laterRows.map((row, i) => ({ ...row, type: 'cycle' as const, invoiceNumber: i + 2 }))
    ];

    const damageInvoiceRows = (props.order.orderTrail || [])
      .filter(event => event.eventType === OrderEvent.DAMAGE_INVOICE)
      .map(event => ({
        type: 'damage' as const,
        time: event.time,
        amount: Number(event.value || 0),
        remark: event.comment
      }));

    return [...cycleRows, ...damageInvoiceRows];
  }
  async function handleDownloadInvoiceRow(invoiceNumber: number) {
    setInvoiceDownloadingNumber(invoiceNumber);
    try {
      const response = await GobikesApi.get(
        `/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/invoice-history/${invoiceNumber}/pdf`,
        { headers: { 'vendor-token': authCookie }, responseType: 'blob' }
      );
      if (response?.data) {
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${props.order.id}_${invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      props.setToastState({ open: true, message: 'Failed to download invoice, please retry' });
    }
    setInvoiceDownloadingNumber(null);
  }
  async function handleDownloadDamageInvoice(time: number) {
    setDamageDownloadingTime(time);
    try {
      const response = await GobikesApi.get(
        `/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/damage-invoice/${time}/pdf`,
        { headers: { 'vendor-token': authCookie }, responseType: 'blob' }
      );
      if (response?.data) {
        const url = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `damage_invoice_${props.order.id}_${time}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      props.setToastState({ open: true, message: 'Failed to download invoice, please retry' });
    }
    setDamageDownloadingTime(null);
  }

  function renderInvoiceHistoryRows() {
    const rows = getInvoiceHistoryRows();
    if (!rows.length) return <label className="label">No invoices yet</label>;
    return (
      <Fragment>
        {rows.map((row, i) => row.type === 'damage' ? (
          <div key={`damage-${i}`} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Damage Invoice</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700 }}>₹ {Number(row.amount).toFixed(2)}</span>
                <Button
                  size="small"
                  disabled={damageDownloadingTime === row.time}
                  onClick={() => handleDownloadDamageInvoice(row.time)}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <Download fontSize="small" />
                </Button>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#777', marginTop: 4 }}>
              Created on {DateTime.fromMillis(row.time).toFormat('MMM d, yyyy, h:mm a')}
            </div>
            {row.remark && (
              <div style={{ marginTop: 4, fontSize: '13px', color: '#555' }}>
                {row.remark}
              </div>
            )}
          </div>
        ) : (
          <div key={row.invoiceNumber} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Invoice #{row.invoiceNumber}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700 }}>₹ {Number(row.amount).toFixed(2)}</span>
                <Button
                  size="small"
                  disabled={invoiceDownloadingNumber === row.invoiceNumber}
                  onClick={() => handleDownloadInvoiceRow(row.invoiceNumber)}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <Download fontSize="small" />
                </Button>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#777', marginTop: 4 }}>
              {DateTime.fromMillis(row.cycleStart).toFormat('MMM d, h:mm a')} - {DateTime.fromMillis(row.cycleEnd).toFormat('MMM d, h:mm a')}
            </div>
            {row.createdAt && (
              <div style={{ fontSize: '12px', color: '#777', marginTop: 2 }}>
                Created on {DateTime.fromISO(row.createdAt).toFormat('MMM d, yyyy, h:mm a')}
              </div>
            )}
            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              {row.isPaid ? (
                <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: '13px' }}>
                  {props.order.paymentMode === PaymentMode.OFFLINE ? 'Cash Paid' : 'Paid Online'}
                  {row.paidAt ? ` – ${DateTime.fromMillis(Number(row.paidAt)).toFormat('MMM d, h:mm a')}` : ''}
                </span>
              ) : (
                <Fragment>
                  <span style={{ color: '#e07b00', fontWeight: 600, fontSize: '13px' }}>Pending</span>
                  {row.paymentLink && (
                    <Button
                      size="small"
                      onClick={() => { navigator.clipboard.writeText(row.paymentLink as string); props.setToastState({ open: true, message: 'Payment link copied!' }); }}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      <ContentCopyRounded fontSize="small" />
                    </Button>
                  )}
                </Fragment>
              )}
            </div>
          </div>
        ))}
      </Fragment>
    );
  }

  // Customer details — phone card-section layout
  function renderCustomerDetails() {
    return <Paper className="gbk-paper booking-detail" sx={{ width: 'calc(100% - 2px)' }} elevation={0}>
      {/* ---- NEW PHONE CARD-SECTION LAYOUT ---- */}
      <div className="bd-tab-content">

        {/* ── Profile Header ── */}
        <div className="bd-profile-card">
          <div className="bd-avatar">
            {(props.order.user?.firstName?.[0] || '?').toUpperCase()}{(props.order.user?.lastName?.[0] || '').toUpperCase()}
          </div>
          <div className="bd-profile-text">
            <div className="bd-profile-name">{props.order.user?.firstName} {props.order.user?.lastName}</div>
            <div className="bd-profile-sub">{props.order.user?.email || '—'}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#5a8f1a', lineHeight: 1 }}>{Math.max(0, (props.order.user?.confirmedOrders || 0) - 1)}</div>
            <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Past Rides</div>
          </div>
          {props.order.user?.reputeAttributes?.latestPic?.doc && (
            <button className="bd-photo-btn" onClick={viewProfilePic}>Photo</button>
          )}
        </div>

        {/* ── Contact Info ── */}
        <div className="bd-section">
          <div className="bd-section-header">Contact</div>
          <div className="bd-info-list">

            <div className="bd-info-row">
              <Call className="bd-info-icon" />
              <div className="bd-info-text">
                <div className="bd-info-label">Phone</div>
                <div className="bd-info-value">
                  {props.order.user?.countryCode}-{props.order.user?.phone}
                  {props.order.user?.otherPhone && <span style={{ color: '#888', fontSize: '12px' }}>, {props.order.user.otherPhone}</span>}
                  {props.order.emPhone && <span style={{ color: '#888', fontSize: '12px' }}>, {props.order.emPhone}</span>}
                </div>
              </div>
              <a href={`tel:${props.order.user?.phone}`} className="bd-pill-btn">Call</a>
            </div>

            {props.order.bookingLocation && (
              <div className="bd-info-row">
                <LocationOn className="bd-info-icon" />
                <div className="bd-info-text">
                  <div className="bd-info-label">Customer's Location at the Time of Booking</div>
                  <div className="bd-info-value" style={{ color: '#333' }}>{props.order.bookingLocation.label}</div>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${props.order.bookingLocation.lat},${props.order.bookingLocation.lng}`}
                  target="_blank" rel="noopener noreferrer"
                  className="bd-pill-btn"
                >Map</a>
              </div>
            )}

            {props.order.localAddress && (
              <div className="bd-info-row">
                <Home className="bd-info-icon" />
                <div className="bd-info-text">
                  <div className="bd-info-label">Local Address</div>
                  <div className="bd-info-value">{props.order.localAddress}</div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── Relationship with You ── */}
        <div className="bd-section">
          <div className="bd-section-header">Relationship with You</div>
          <div className="bd-info-list">

            <div className="bd-info-row">
              <CurrencyRupee className="bd-info-icon" />
              <div className="bd-info-text">
                <div className="bd-info-label">Lifetime Revenue</div>
                <div className="bd-info-value">₹{(props.order.user?.vendorStats?.lifetimeRevenue || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="bd-info-row">
              <DateRange className="bd-info-icon" />
              <div className="bd-info-text">
                <div className="bd-info-label">Total Rental Days</div>
                <div className="bd-info-value">{props.order.user?.vendorStats?.totalRentalDays || 0}</div>
              </div>
            </div>

            <div className="bd-info-row">
              <TaskAlt className="bd-info-icon" />
              <div className="bd-info-text">
                <div className="bd-info-label">Total Confirmed Orders</div>
                <div className="bd-info-value">{props.order.user?.vendorStats?.totalConfirmedOrders || 0}</div>
              </div>
            </div>

          </div>
        </div>

        {/* ── Documents ── */}
        <div className="bd-section">
          <div className="bd-section-header">Documents</div>
          <div className="bd-info-list">

            {/* DL row */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f2f2f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a' }}>Driving License</div>
                <div
                  className="bd-doc-status-chip"
                  style={{ background: verificationToColor[dlVerified] + '18', color: verificationToColor[dlVerified] }}
                >
                  {verificationToIcon[dlVerified]}{dlVerified}
                </div>
              </div>
              {(props.order.user?.reputeAttributes?.dl?.doc?.db_image || props.order.user?.reputeAttributes?.dl?.doc?.dl_pdf || props.order.user?.reputeAttributes?.dl?.doc?.dl_front) && (
                <div className="bd-doc-downloads">
                  {props.order.user?.reputeAttributes?.dl?.doc?.db_image && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('dl/db_image', 'dl_db_image.jpg')}>Photo</button>
                  )}
                  {props.order.user?.reputeAttributes?.dl?.doc?.dl_pdf && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('dl/dl_pdf', 'DRVLC.pdf')}>DL PDF</button>
                  )}
                  {props.order.user?.reputeAttributes?.dl?.doc?.dl_front && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('dl/dl_front', 'DRVLC_thumbnail.jpg')}>DL Image</button>
                  )}
                </div>
              )}
            </div>

            {/* Aadhar row */}
            <div style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a' }}>Aadhar Card</div>
                <div
                  className="bd-doc-status-chip"
                  style={{ background: verificationToColor[aadharVerified] + '18', color: verificationToColor[aadharVerified] }}
                >
                  {verificationToIcon[aadharVerified]}{aadharVerified}
                </div>
              </div>
              {(props.order.user?.reputeAttributes?.aadhar?.doc?.db_image || props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_pdf || props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_front) && (
                <div className="bd-doc-downloads">
                  {props.order.user?.reputeAttributes?.aadhar?.doc?.db_image && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('aadhar/db_image', 'aadhaar_image.jpg')}>Photo</button>
                  )}
                  {props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_pdf && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('aadhar/aadhaar_pdf', 'ADHAR.pdf')}>Aadhar PDF</button>
                  )}
                  {props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_front && (
                    <button className="bd-doc-download-btn" onClick={() => downloadFile('aadhar/aadhaar_front', 'ADHAR_thumbnail.jpg')}>Aadhar Image</button>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Doc action buttons */}
          {(dlVerified !== 'Verified' || aadharVerified !== 'Verified') && (
            <div className="bd-action-group" style={{ marginTop: 10 }}>
              <LoadingButton
                size="small"
                startIcon={<Refresh />}
                variant="outlined"
                className="thick-border"
                loading={Boolean(localState.refreshingDocs)}
                onClick={async () => {
                  setLocalState({ refreshingDocs: true });
                  try {
                    await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/refresh`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                    props.backFn(true, props.order.id);
                  } catch (e: any) {
                    setLocalState({ message: (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred' });
                  }
                }}
                sx={{ borderRadius: '10px !important', textTransform: 'none', fontWeight: 600 }}
              >
                Refresh Documents
              </LoadingButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoadingButton
                  size="small"
                  startIcon={<Refresh />}
                  variant="outlined"
                  className="thick-border"
                  loading={Boolean(localState.sendingLink)}
                  onClick={async () => {
                    setLocalState({ sendingLink: true });
                    try {
                      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/resend`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                      props.backFn(true, props.order.id, { open: true, message: 'SMS sent to customer!' });
                    } catch (e: any) {
                      setLocalState({ message: (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred' });
                    }
                  }}
                  sx={{ borderRadius: '10px !important', textTransform: 'none', fontWeight: 600 }}
                >
                  Send Verification Link
                </LoadingButton>
                {props.order.user?.reputeUrl && (
                  <Button size="small" onClick={() => { navigator.clipboard.writeText(props.order.user?.reputeUrl || ''); props.setToastState({ open: true, message: 'Verification link copied!' }); }}
                    sx={{ minWidth: 'auto', borderRadius: '8px' }}>
                    <ContentCopyRounded fontSize="small" />
                  </Button>
                )}
              </Box>
              {localState.message && (
                <div style={{ color: Colors.failureRed, fontSize: '13px', padding: '4px 0' }}>{localState.message}</div>
              )}
            </div>
          )}
        </div>

        {/* ── Customer Review ── */}
        {props.order.orderMode !== OrderMode.VENDOR && (
          <div className="bd-section">
            <div className="bd-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Customer Review</span>
              {(props.order.bookingStatus === BookingStatus.CONFIRMED || props.order.bookingStatus === BookingStatus.EXTENDED) && (
                <LoadingButton size="small" startIcon={<Refresh />} variant="outlined" className="thick-border"
                  loading={Boolean(localState.requestingReview)}
                  onClick={async () => {
                    setLocalState({ requestingReview: true });
                    try {
                      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/request-review`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                      props.backFn(true, props.order.id, { open: true, message: 'SMS and mail sent to customer!' });
                    } catch (e: any) {
                      setLocalState({ message: (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred' });
                    }
                  }}
                  sx={{ borderRadius: '10px !important', textTransform: 'none', fontWeight: 600, fontSize: '11px' }}>
                  {props.order.reviews?.[0] ? 'Ask again' : 'Ask for review'}
                </LoadingButton>
              )}
            </div>
            {props.order.reviews?.[0] ? (
              <div className="bd-card">
                {Object.keys(ratingToNameMap).map(f => (
                  <div key={f} className="bd-review-row">
                    <span className="bd-review-label">{ratingToNameMap[f as keyof ReviewModel]}</span>
                    <div className="bd-review-stars">
                      {Array.from(Array(Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()).map((_, i) => (
                        <Star key={i} style={{ fontSize: '14px', color: '#f7c000' }} />
                      ))}
                      {Array.from(Array(5 - Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()).map((_, i) => (
                        <Star key={i} style={{ fontSize: '14px', color: '#ddd' }} />
                      ))}
                    </div>
                  </div>
                ))}
                {props.order.reviews[0].message && (
                  <p style={{ fontSize: '13px', color: '#666', marginTop: '10px', fontStyle: 'italic', marginBottom: 0 }}>
                    "{props.order.reviews[0].message}"
                  </p>
                )}
              </div>
            ) : (
              <div className="bd-card" style={{ color: '#bbb', fontSize: '14px', textAlign: 'center', padding: '20px 16px' }}>
                No review yet
              </div>
            )}
          </div>
        )}

        {renderExtraGap()}
      </div>
      {/* ---- END PHONE LAYOUT — original desktop content hidden on phone via CSS ---- */}
      <div style={{ display: 'none' }}>
      <div className="content">

        {props.order.user?.reputeAttributes?.latestPic?.doc && (
          <div style={{ marginBottom: 12 }}>
            <Button size="small" variant="outlined" onClick={viewProfilePic}>View Profile Pic</Button>
          </div>
        )}

        {/* First column - Name, Email, Phone */}
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <div className="gbk-field">
            <label className="label">Name</label> <br />
            <label className="value">{props.order.user?.firstName} {props.order.user?.lastName}</label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Email</label> <br />
            <label className="value">{props.order.user?.email || '-'}</label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Contact</label> <br />
            <label className="value">{props.order.user?.countryCode}-{props.order.user?.phone}{props.order.user && props.order.user.otherPhone ? `,$ ${props.order.user.otherPhone}` : ''}{props.order.emPhone ? `, ${props.order.emPhone}` : ''} </label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Customer&apos;s Location at the Time of Booking</label> <br />
            <label className="value">
              {props.order.bookingLocation ? (
                <a
                  href={`https://www.google.com/maps?q=${props.order.bookingLocation.lat},${props.order.bookingLocation.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {props.order.bookingLocation.label}
                </a>
              ) : (
                <span className="grey-text-light italic" style={{ fontSize: isPhone ? '14px' : '16px' }}>Not available</span>
              )}
            </label>
          </div>
          <Box sx={separatorSx} style={{ ...(!props.order.localAddress && { display: 'none' }) }}></Box>
          <div className="gbk-field" style={{ ...(!props.order.localAddress && { display: 'none' }) }}>
            <label className="label">Local address</label> <br />
            <label className="value">{props.order.localAddress ? props.order.localAddress : <span className="grey-text-light italic" style={{ fontSize: isPhone ? '14px' : '16px' }}>Available after ride start</span>}</label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Previous orders</label> <br />
            <label className="value">{props.order.user?.confirmedOrders || 0}</label>
          </div>
        </div>

        {/* Second column - Documents */}
        <div className="gbk-col" style={{ minWidth: 300 }}>
          {/* ID Proof */}
          <div className="gbk-field">
            <label className="label">ID Proof</label> <br />
            {/* DL */}
            {
              props.order.user?.reputeAttributes?.dl?.verified ? (
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <label className="value">
                    Driving License
                    {
                      <span className="gbk-icon-span" style={{ color: verificationToColor[dlVerified], paddingLeft: 10 }}>
                        {verificationToIcon[dlVerified]}{dlVerified}
                      </span>
                    }
                  </label><br />
                  {
                    (props.order.user?.reputeAttributes?.dl?.doc?.db_image ? (
                      <div
                        className="gbk-thumb download"
                        style={{ marginTop: 10 }}
                        onClick={() => downloadFile('dl/db_image', 'dl_db_image.jpg')}
                      >
                        Photo &nbsp;
                        <span className="gbk-icon-span"><Download /></span>
                      </div>
                    ) : '')
                  }
                  {
                    (props.order.user?.reputeAttributes?.dl?.doc?.dl_pdf ? (
                      <div
                        className="gbk-thumb download"
                        style={{ marginTop: 10 }}
                        onClick={() => downloadFile('dl/dl_pdf', 'DRVLC.pdf')}
                      >
                        DL PDF &nbsp;
                        <span className="gbk-icon-span"><Download /></span>
                      </div>
                    ) : '')
                  }
                  {
                    (props.order.user?.reputeAttributes?.dl?.doc?.dl_front ? (
                      <div
                        className="gbk-thumb download"
                        style={{ marginTop: 10 }}
                        onClick={() => downloadFile('dl/dl_front', 'DRVLC_thumbnail.jpg')}
                      >
                        DL Image &nbsp;
                        <span className="gbk-icon-span"><Download /></span>
                      </div>
                    ) : '')
                  }
                </div>
              ) : (
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <label className="value">
                    Driving License
                    {
                      <span className="gbk-icon-span" style={{ color: verificationToColor[dlVerified], paddingLeft: 10 }}>
                        {verificationToIcon[dlVerified]}{dlVerified}
                      </span>
                    }
                  </label><br />
                  <div className="gbk-thumb empty" style={{ marginTop: 10 }}></div>
                </div>
              )
            }
            {/* AADHAR */}
            {props.order.user?.reputeAttributes?.aadhar?.verified ? (
              <div className="gbk-col" style={{ minWidth: 300 }}>
                <label className="value">
                  Aadhar Card: Image
                  {
                    <span className="gbk-icon-span" style={{ color: verificationToColor[aadharVerified], paddingLeft: 10 }}>
                      {verificationToIcon[aadharVerified]}{aadharVerified}
                    </span>
                  }
                </label><br />
                {
                  (props.order.user?.reputeAttributes?.aadhar?.doc?.db_image ? (
                    <div
                      className="gbk-thumb download"
                      style={{ marginTop: 10 }}
                      onClick={() => downloadFile('aadhar/db_image', 'aadhaar_image.jpg')}
                    >
                      Photo &nbsp;
                      <span className="gbk-icon-span"><Download /></span>
                    </div>
                  ) : '')
                }
                {
                  (props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_pdf ? (
                    <div
                      className="gbk-thumb download"
                      style={{ marginTop: 10 }}
                      onClick={() => downloadFile('aadhar/aadhaar_pdf', 'ADHAR.pdf')}
                    >
                      Aadhar PDF &nbsp;
                      <span className="gbk-icon-span"><Download /></span>
                    </div>
                  ) : '')
                }
                {
                  (props.order.user?.reputeAttributes?.aadhar?.doc?.aadhaar_front ? (
                    <div
                      className="gbk-thumb download"
                      style={{ marginTop: 10 }}
                      onClick={() => downloadFile('aadhar/aadhaar_front', 'ADHAR_thumbnail.jpg')}
                    >
                      Aadhar Image &nbsp;
                      <span className="gbk-icon-span"><Download /></span>
                    </div>
                  ) : '')
                }
              </div>
            ) : (
              <div className="gbk-col" style={{ minWidth: 300 }}>
                <label className="value">
                  Aadhar Card
                  {
                    <span className="gbk-icon-span" style={{ color: verificationToColor[aadharVerified], paddingLeft: 10 }}>
                      {verificationToIcon[aadharVerified]}{aadharVerified}
                    </span>
                  }
                </label><br />
                <div className="gbk-thumb empty" style={{ marginTop: 10 }}></div>
              </div>
            )}
          </div>
          <br />
          {/* Refresh repute docs */}
          {
            (dlVerified !== 'Verified' || aadharVerified !== 'Verified')
              ? (
                <div className="gbk-field" style={{ marginTop: 30, width: '100%' }}>
                  <LoadingButton
                    size="small"
                    startIcon={<Refresh />}
                    variant={'outlined'}
                    className="thick-border"
                    loading={Boolean(localState.refreshingDocs)}
                    onClick={async () => {
                      setLocalState({ refreshingDocs: true });
                      try {
                        await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/refresh`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                        props.backFn(true, props.order.id);
                      } catch (e: any) {
                        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                        setLocalState({ message: errorMessage })
                      }
                    }}
                  >
                    Refresh documents
                  </LoadingButton>
                </div>
              ) : ''
          }
          {/* Send verification link button */}
          {
            (dlVerified !== 'Verified' || aadharVerified !== 'Verified')
              ? (
                <div className="gbk-field" style={{ marginTop: 30, width: '100%' }}>
                  {/* Send link button */}
                  <LoadingButton
                    size="small"
                    startIcon={<Refresh />}
                    variant={'outlined'}
                    className="thick-border"
                    loading={Boolean(localState.sendingLink)}
                    onClick={async () => {
                      setLocalState({ sendingLink: true });
                      try {
                        await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/resend`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                        props.backFn(true, props.order.id, { open: true, message: 'SMS sent to customer!' });
                      } catch (e: any) {
                        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                        setLocalState({ message: errorMessage })
                      }
                    }}
                  >
                    Send Verification Link
                  </LoadingButton>
                  {/* Copy link */}
                  {
                    props.order.user?.reputeUrl ? (
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(props.order.user?.reputeUrl || '');
                          props.setToastState({ open: true, message: 'Verification link copied!' })
                        }}
                      >
                        <ContentCopyRounded />
                      </Button>
                    ) : ''
                  }
                </div>
              )
              : ''
          }
          {/* Error message */}
          <div className="gbk-field" style={{ marginTop: 30, color: Colors.failureRed }}>
            {localState.message}
          </div>
        </div>
        {/* Third column - Customer photo */}
        {/* <div className="gbk-col" style={{ minWidth: 300 }}>
          <div className="gbk-field">
            <label className="label">Customer photo with bike</label> <br />
            <label className="value">
              {
                props.order.photo
                  ? <Button className="thick-border" variant="outlined" size="large" startIcon={<AddAPhoto />} > Add Photo</Button>
                  : <Button className="thick-border" variant="outlined" size="large" startIcon={<AddAPhoto />} > Add Photo</Button>
              }
            </label>
          </div>
        </div> */}
      </div>

      {/* Review */}
      {props.order.orderMode === OrderMode.VENDOR ? '' : (
        <div className="content">
          <div className="gbk-col" style={{ minWidth: 600 }}>
            <div className="gbk-field">
              {props.order.reviews && props.order.reviews[0] ? (
                <div>
                  <div className="vat" style={{ width: 200, fontSize: isPhone ? '14px' : '18px', lineHeight: isPhone ? '22px' : '30px', fontWeight: 'bold' }}>Customer Review:</div>
                  {Object.keys(ratingToNameMap).map(f => (
                    <div key={f}>
                      <div className="inb vat" style={{ width: 200, fontSize: isPhone ? '14px' : '18px', lineHeight: isPhone ? '22px' : '30px' }}>{ratingToNameMap[f as keyof ReviewModel]}</div>
                      <div className="inb vat" style={{ width: 200 }}>
                        {Array.from(Array(Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()).map((_, i) => <span key={i} className={`gbk-icon-span ${isPhone ? 'fs-16' : 'large'}`}><Star className="rating-star" /></span>)}
                        {Array.from(Array(5 - Math.floor(Number(props.order.reviews?.[0][f as keyof ReviewModel]))).keys()).map((_, i) => <span key={i} className={`gbk-icon-span ${isPhone ? 'fs-16' : 'large'}`}><Star className="grey-text-medium" /></span>)}
                      </div>
                    </div>
                  ))}
                  <div style={{ paddingTop: '20px' }}>
                    <div className="vat" style={{ width: 200, fontSize: isPhone ? '14px' : '18px', lineHeight: isPhone ? '22px' : '30px', fontWeight: 'bold' }}>Customer Comments:</div>
                    <div className="inb vat" style={{ width: 200, fontSize: isPhone ? '14px' : '18px', lineHeight: isPhone ? '22px' : '30px' }}>
                      {props.order.reviews?.[0].message || 'No message added'}
                    </div>
                  </div>
                </div>
              ) : (
                <label className="label">No customer review available</label>
              )}
              <br /><br />
              {/* <Box className="value" > */}
              <Box sx={{ width: 'auto' }} hidden={props.order.bookingStatus !== BookingStatus.CONFIRMED && props.order.bookingStatus !== BookingStatus.EXTENDED}>
                <LoadingButton
                  size="small"
                  startIcon={<Refresh />}
                  variant={'outlined'}
                  className="thick-border"
                  loading={Boolean(localState.requestingReview)}
                  onClick={async () => {
                    setLocalState({ requestingReview: true });
                    try {
                      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/request-review`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                      props.backFn(true, props.order.id, { open: true, message: 'SMS and mail sent to customer!' });
                    } catch (e: any) {
                      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                      setLocalState({ message: errorMessage })
                    }
                  }}
                >
                  Ask for review{props.order.reviews && props.order.reviews[0] ? ' again' : ''}
                </LoadingButton>
                &nbsp;
                {/* <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${Urls.MAIN_SITE}/order-review?order_id=${props.order.id}`);
                    props.setToastState({ open: true, message: 'Review link copied!' })
                  }}
                >
                  <ContentCopyRounded />
                </Button> */}
                {/* <span onClick={() => { navigator.clipboard.writeText(`${Urls.MAIN_SITE}/order-review?order_id=${props.order.id}`); props.setToastState({ open: true, message: 'Review link copied!' }) }} className="gbk-icon-span link-icon" style={{ marginLeft: '10px' }}>
                  </span> */}
              </Box>
              {/* </Box> */}
              <br />
            </div>
            <br />
          </div>
        </div>
      )}

      {renderExtraGap()}
      </div>{/* end hidden old content */}
    </Paper >
  }

  // Booking details
  function renderBookingDetails() {
    return <Paper className="gbk-paper booking-detail" sx={{ width: 'calc(100% - 2px)' }} elevation={0}>
      <div className="content">

        {/* First column - Scheduled times */}
        <div className="gbk-col" style={{ minWidth: 300 }}>
          {isPhone ? (
            <div style={{ display: 'flex', gap: '8px', padding: '14px 14px 0' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Pickup Date and Time</div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{DateTime.fromMillis(Number(props.order.rideStartScheduled)).toLocaleString(DateTime.DATETIME_MED)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Drop-off Date and Time</div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{DateTime.fromMillis(Number(props.order.rideEndScheduled)).toLocaleString(DateTime.DATETIME_MED)}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="gbk-field">
                <label className="label">Pickup Date and Time</label> <br />
                <label className="value">{DateTime.fromMillis(Number(props.order.rideStartScheduled)).toLocaleString(DateTime.DATETIME_MED)}</label>
              </div>
              <Box sx={{ width: '10px', height: '20px' }}></Box>
              <div className="gbk-field">
                <label className="label">Drop-off Date and Time</label> <br />
                <label className="value">{DateTime.fromMillis(Number(props.order.rideEndScheduled)).toLocaleString(DateTime.DATETIME_MED)}</label>
              </div>
            </>
          )}
          {isPhone && <>
            <Box sx={{ height: '14px' }}></Box>
            <Box sx={{ borderTop: '1px solid #e8e8e8', mx: '14px' }}></Box>
            <div style={{ display: 'flex', gap: '12px', padding: '10px 14px' }}>
              {props.order.orderMode === OrderMode.VENDOR && (
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Source</div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>SELF</div>
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Booking Status</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: bookingStatusToColor[props.order.bookingStatus] }}>
                  {props.order.bookingStatus.charAt(0) + props.order.bookingStatus.slice(1).toLowerCase()}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Ride Status</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: rideStatusToColor[props.order.rideStatus] }}>
                  {RideStatusToText[props.order.rideStatus]}
                </div>
              </div>
            </div>
          </>}
          <Box sx={separatorSx}></Box>
        </div>

        {/* Second column - Odometer */}
        <div className="gbk-col" style={{ minWidth: 300 }}>
          {isPhone ? (
            <div style={{ display: 'flex', gap: '8px', padding: '14px 14px 0' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Odometer Start Reading</div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odStart : '-'}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>Odometer End Reading</div>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odEnd || '-' : '-'}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="gbk-field">
                <label className="label">Odometer Start Reading</label> <br />
                <label className="value">{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odStart : '-'}</label>
              </div>
              <Box sx={separatorSx}></Box>
              <div className="gbk-field">
                <label className="label">Odometer End Reading</label> <br />
                <label className="value">{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odEnd || '-' : '-'}</label>
              </div>
            </>
          )}
          <Box sx={separatorSx}></Box>
        </div>

        {/* Third column - Ride status */}
        <div className={`gbk-col bd-hide-phone`} style={{ minWidth: 300 }}>
          <div className="gbk-field">
            <label className="label">Booking status</label> <br />
            <label className="value" style={{ color: bookingStatusToColor[props.order.bookingStatus], fontWeight: 'bold' }}>
              {props.order.bookingStatus.charAt(0) + props.order.bookingStatus.slice(1).toLowerCase()}
            </label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Ride status</label> <br />
            <label className="value" style={{ color: rideStatusToColor[props.order.rideStatus], fontWeight: 'bold' }}>
              {RideStatusToText[props.order.rideStatus]}
            </label>
          </div>
          {/* <Box sx={separatorSx}></Box>
          <div className="gbk-field" style={{ ...(!props.order.cancelDetails?.reason && { display: 'none' }) }}>
            <label className="label">Cancellation reason</label> <br />
            <label className="value">{props.order.cancelDetails?.reason}</label>
          </div> */}
        </div>

        {/* Limits and Penalties */}
        {
          props.order.staticData ?
            <div className="gbk-col" style={{ minWidth: 300 }}>
              <div className="gbk-field">
                <label className="label">Distance limit</label>
                <label className="value">{props.order.staticData?.distanceLimit && props.order.staticData?.distanceLimit > 0 ? `${props.order.staticData.distanceLimit} Km` : 'No limit'}</label>
              </div>
              <Box sx={separatorSx}></Box>
              <div className="gbk-field">
                <label className="label">Extra KM charge</label>
                <label className="value">₹ {props.order.staticData?.penalty?.distance || 0} / Km</label>
              </div>
              {
                (props.order.staticData.finalPenalties || []).map((penalty, i) => (
                  <Fragment>
                    <Box sx={separatorSx}></Box>
                    <div key={i} className="gbk-field">
                      <label className="label">{capitalizeFirstLetter(penalty.type)} penalty</label> <br />
                      <label className="value">₹ {penalty.amount}</label>
                    </div>
                  </Fragment>
                ))
              }
            </div>
            : ''
        }
        {/* <div className="gbk-col" style={{ minWidth: 300 }}>
          <div className="gbk-field">
            <label className="label">Distance penalty</label> <br />
            <label className="value">{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odStart : '-'}</label>
          </div>
          <Box sx={separatorSx}></Box>
          <div className="gbk-field">
            <label className="label">Odometer End Reading</label> <br />
            <label className="value">{props.order?.bikeDetails?.length ? props.order?.bikeDetails[0].odEnd || '-' : '-'}</label>
          </div>
          <Box sx={separatorSx}></Box>
        </div> */}
        <br />

        {/* Delivery details */}
        {isPhone ? <br /> : ''}
        {
          (props.order.deliveryDetails && props.order.deliveryDetails.required) ? (
            <div className="gbk-col" style={{ minWidth: 400 }}>
              <div className="gbk-field" style={{ width: '100%' }}>
                <label className="label">Delivery Details</label> <br />
                <label className="value">Delivery required: <b>Yes</b></label> <br />
                <label className="value">Delivery address: <b>{props.order.deliveryDetails.address || 'No address provided'}</b></label> <br />
                <label className="value">Delivery charges: <b>{props.order.deliveryAmount ? `₹ ${props.order.deliveryAmount}` : '-'}</b></label> <br />
                <label className="value">
                  Delivery location:{' '}
                  {props.order.deliveryDetails.address ? (
                    <a
                      href={`https://www.google.com/maps?q=${encodeURIComponent(props.order.deliveryDetails.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on map
                    </a>
                  ) : (
                    <span className="grey-text-light italic">not available</span>
                  )}
                </label>
              </div>
            </div>
          ) : ''
        }

        <br />
        <br />
        {/* Uploaded images */}
        {
          props.order.imageData ? (
            <div className="gbk-col" style={{ minWidth: 300 }} >
              <div className="gbk-field">
                <label className="label">Pickup photos</label> <br />
              </div>
              <Box sx={separatorSx}></Box>
              {isPhone ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                  {Object.keys(props.order?.imageData as object).map(label => (
                    props.order.imageData && props.order.imageData[label as OrderUploadLabels] ? (
                      <div key={label} style={{ flex: '1 1 calc(33.33% - 6px)', minWidth: 0 }}>
                        <UploadImageViewer
                          label={label as OrderUploadLabels}
                          awsKey={props.order.imageData[label as OrderUploadLabels] || 'No label'}
                        />
                      </div>
                    ) : ''
                  ))}
                </div>
              ) : (
                Object.keys(props.order?.imageData as object).map(label => (
                  props.order.imageData && props.order.imageData[label as OrderUploadLabels] ? (
                    <UploadImageViewer
                      key={label}
                      label={label as OrderUploadLabels}
                      awsKey={props.order.imageData[label as OrderUploadLabels] || 'No label'}
                    />
                  ) : ''
                ))
              )}
            </div>
          ) : ''
        }

        {/* Booking details — vehicle + pricing combined card on phone */}
        <div className="bd-vehicle-section">
          {/* Name row: vehicle name left, change vehicle right */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <Typography fontSize={isPhone ? '13px' : undefined} style={{ flex: 1, minWidth: 0 }}>
              <b>{`${props.order?.bikes?.[0].brand} ${props.order?.bikes?.[0].name}`}{props.order?.bikes?.[0].vehicleType === 'CAR' ? ` (${props.order.bikes[0].startingMethod === 'AUTO' ? 'Automatic' : props.order.bikes[0].startingMethod}, ${props.order.bikes[0].engineType?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase())})` : ''}</b>
            </Typography>
            <div
              hidden={props.order.rideStatus === RideStatus.ENDED || ![BookingStatus.CONFIRMED, BookingStatus.EXTENDED].includes(props.order.bookingStatus)}
              style={{ flexShrink: 0 }}
            >
              <EditBooking style={{}} order={props.order} hub={selectedHub} backFn={() => props.backFn(true, props.order.id)}>
                <Button
                  startIcon={<Edit />}
                  variant={'contained'}
                  className="thick-border no-text-transform"
                  size="small"
                  sx={{ fontSize: isPhone ? '11px' : undefined }}
                >
                  Change vehicle
                </Button>
              </EditBooking>
            </div>
          </div>
          {/* Qty + RC numbers */}
          {isPhone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '6px 0 4px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>
                Qty: {orderBreakDown[0]?.quantity || props.order.bikes?.length || 1}
              </span>
              {props.order.bikeDetails?.length ? (
                <span style={{ fontSize: '12px', color: '#555' }}>
                  • {props.order.bikeDetails.map(b => b.rcNumber).join(', ')}
                </span>
              ) : (
                <span style={{ fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>RC available after ride start</span>
              )}
            </div>
          )}
          {/* Pricing rows below, inside the same card on phone */}
          <div style={{ clear: 'both' }}>
            <DataGrid
              disableColumnMenu={true}
              disableColumnFilter={true}
              density='compact'
              className='gbk-dg phone'
              rowHeight={120}
              hideFooter={true}
              headerHeight={0}
              autoHeight={true}
              rows={orderBreakDown}
              columns={[{
                field: 'package',
                width: 200,
                align: 'center',
                renderCell: (o: { row: BikePriceBreakdownTableEntry }) => `${packageNameToString[o.row.package]}`
              }, {
                field: 'breakdown',
                width: 200,
                align: 'center',
                renderCell: (o: { row: BikePriceBreakdownTableEntry }) => `${o.row.count > 1 ? `${o.row.count} x ` : ''}${o.row.unitQty} ${unitNameToString[o.row.unitName]}${o.row.unitQty > 1 ? 's' : ''} * ₹ ${o.row.unitPrice} * ${o.row.quantity} ${vehicleLabel}${o.row.quantity > 1 ? 's' : ''}`
              }, {
                field: 'amount',
                width: 200,
                align: 'center',
                renderCell: (o: { row: BikePriceBreakdownTableEntry }) => <span style={{ fontWeight: 'bold' }} >{`₹ ${o.row.amount.toFixed(2)}`}</span>
              }]}
              components={{
                Row: (data: { row: BikePriceBreakdownTableEntry }) => (
                  <div style={{ backgroundColor: '#ebf5d6', padding: isPhone ? '10px 16px' : '10px 20px', borderRadius: '10px', margin: '0 0 10px', fontSize: isPhone ? '12px' : '16px' }}>
                    <span>{`${packageNameToString[data.row.package]}`}</span><br />
                    <span style={{ fontWeight: 'bold', paddingLeft: '20px' }} >
                      {`${data.row.count > 1 ? `${data.row.count} x ` : ''}${data.row.unitQty} ${unitNameToString[data.row.unitName]}${data.row.unitQty > 1 ? 's' : ''} * ₹ ${data.row.unitPrice} * ${data.row.quantity} ${vehicleLabel}${data.row.quantity > 1 ? 's' : ''}`}
                      =
                    </span>
                    <span style={{ fontWeight: 'bold', color: Colors.primary }} >{`₹ ${data.row.amount.toFixed(2)}`}</span>
                  </div>
                )
              }}
              pageSize={100}
              rowsPerPageOptions={[5]}
              disableSelectionOnClick
            />
          </div>
        </div>
        <br />
        <br />
        {accBreakDown.map((a, i) => (
          <Fragment key={i}>
            <Typography>
              <b>{a.name}</b>
            </Typography>
            <div style={{ height: 65 + 1 * 76 }}>
              <DataGrid
                disableColumnMenu={true}
                disableColumnFilter={true}
                density='compact'
                className='gbk-dg phone'
                rowHeight={120}
                hideFooter={true}
                headerHeight={0}
                autoHeight={true}
                rows={[a]}
                columns={[{
                  field: 'package',
                  width: 200,
                  align: 'center',
                  renderCell: (o: { row: AccPriceBreakdownTableEntry }) => `${packageNameToString[o.row.package]}`
                }, {
                  field: 'breakdown',
                  width: 200,
                  align: 'center',
                  renderCell: (o: { row: AccPriceBreakdownTableEntry }) => `${o.row.unitQty} ${unitNameToString[o.row.unitName]}${o.row.unitQty > 1 ? 's' : ''} * ₹ ${o.row.unitPrice} * ${o.row.quantity} Unit${o.row.quantity > 1 ? 's' : ''}`
                }, {
                  field: 'amount',
                  width: 200,
                  align: 'center',
                  renderCell: (o: { row: AccPriceBreakdownTableEntry }) => <span style={{ fontWeight: 'bold' }} >{`₹ ${o.row.amount}`}</span>
                }]}
                components={{
                  Row: (data: { row: AccPriceBreakdownTableEntry }) => (
                    <div style={{ backgroundColor: '#ebf5d6', padding: '10px 20px', borderRadius: '10px', margin: '10px 0', fontSize: isPhone ? '12px' : '16px' }}>
                      <span>{`${packageNameToString[data.row.package]}`}</span> <br />
                      <span style={{ fontWeight: 'bold', paddingLeft: '20px' }} >
                        {`${data.row.unitQty} ${unitNameToString[data.row.unitName]}${data.row.unitQty > 1 ? 's' : ''} * ₹ ${data.row.unitPrice} * ${data.row.quantity} ${vehicleLabel}${data.row.quantity > 1 ? 's' : ''}`}
                      </span>=
                      <span style={{ fontWeight: 'bold', color: Colors.primary }} >{`₹ ${data.row.amount.toFixed(2)}`}</span>
                    </div>
                  )
                }}
                pageSize={100}
                rowsPerPageOptions={[5]}
                disableSelectionOnClick
              />
            </div>
          </Fragment>
        ))}
        <br />
        <br /><br /><br />
        <div className="gbk-field bd-hide-phone">
          <label className="label">Booking Date and Time</label> <br />
          <label className="value">{DateTime.fromISO(props.order.createdAt).toLocaleString(DateTime.DATETIME_MED)}</label>
        </div>
        <br /><br />
        <div className="gbk-field bd-hide-phone">
          <label className="label">Booking Source</label> <br />
          <label className="value">{props.order.orderMode === OrderMode.VENDOR ? 'SELF' : 'GoBikes'}</label>
        </div>
        <br /><br />
        <div className="gbk-field bd-hide-phone" hidden={!props.order.bikeDetails}>
          <label className="label">Vehicle Numbers</label> <br />
          <label className="value">
            {
              props.order.bikeDetails ?
                props.order.bikeDetails?.map(b => b.rcNumber).join(',')
                : <span className="grey-text-light italic" style={{ fontSize: isPhone ? '14px' : '16px' }}>Available after ride start</span>
            }</label>
        </div>
        <br /><br />
        <div className={`gbk-field${isPhone && !props.order.orderNotes?.length ? ' bd-hide-phone' : ''}`} hidden={!props.order.bikeDetails}>
          <label className="label">Order Notes</label> <br />
          <label className="value">
            {props.order.orderNotes && props.order.orderNotes.length ? (
              <ul className='gbk-list no-margin'>
                {props.order.orderNotes.map(on => (
                  <li className="fs-14">{on.message}</li>
                ))}
              </ul>
            ) : ''}
            {/* Desktop inline note entry */}
            {!isPhone && (
              <>
                <Box className="gbk-col no-pad-bot" sx={{ width: { md: 600 }, display: orderNoteState.adding ? 'block' : 'none' }}>
                  <GobikesField
                    type={GbkFieldType.STRING}
                    editMode={true}
                    value={orderNoteState.messageValue || ''}
                    updateFn={(v: string) => {
                      setOrderNoteState({ ...orderNoteState, messageValue: v })
                    }}
                  />
                </Box>
                <div className="error-text" style={{ padding: '10px 0 5px 0' }}>{orderNoteState.error}</div>
                <div>
                  <Button style={{ ...(orderNoteState.adding && { display: 'none' }) }} key={'add'} size="small" variant="contained" onClick={() => { setOrderNoteState({ adding: true }) }}>Add note</Button>
                  <Button style={{ ...(!orderNoteState.adding && { display: 'none' }) }} key={'save'} size="small" variant="contained"
                    onClick={async () => {
                      setOrderNoteState({ loading: true });
                      try {
                        await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/notes`, { message: orderNoteState.messageValue }, { headers: { 'vendor-token': authCookie } });
                        props.backFn(true, props.order.id);
                      } catch (e: any) {
                        setOrderNoteState({ ...orderNoteState, error: (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occured' });
                      }
                    }}
                  >Save</Button> &nbsp;&nbsp;
                  <Button style={{ ...(!orderNoteState.adding && { display: 'none' }) }} key={'cancel'} size="small" variant="outlined" onClick={() => { setOrderNoteState({ adding: false }) }}>Cancel</Button>
                </div>
              </>
            )}
          </label>
        </div>
        <br /><br /><br />
        <div className="gbk-field" style={{ width: '100%' }} hidden={!props.order.bikeDetails}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className="label">Booking Timeline</label>
            <Select size="small" value={timelineFilter} onChange={(e) => setTimelineFilter(e.target.value)}>
              {getAvailableTimelineFilterOptions(props.order.orderTrail).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </div>
          <br />
          <label className="value" style={{ width: '100%' }}>
            <Timeline
              sx={{
                [`& .${timelineOppositeContentClasses.root}`]: {
                  flex: 0.5,
                  fontSize: isPhone ? '12px' : '14px',
                },
              }}
            >
              {filterOrderTrail(props.order.orderTrail, timelineFilter).map((event, i) => (
                <TimelineItem key={i}>
                  <TimelineOppositeContent color="textSecondary">
                    {toIstDateString(event.time, { removeDay: true, returnJsx: true })}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={orderEventToDisplayMapping[event.eventType].nodeColor || 'primary'} />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <b>
                      <span className="fs-14">
                        {orderEventToDisplayMapping[event.eventType].text}
                      </span>
                      {(event.eventType === OrderEvent.PAYMENT_LINK || event.eventType === OrderEvent.CUSTOM_PAYMENT_LINK || event.eventType === OrderEvent.OFFLINE_PAYMENT || event.eventType === OrderEvent.DAMAGE_INVOICE) && event.value ? (
                        <Fragment>
                          {event.customFields?.link ? (
                            <span onClick={() => { navigator.clipboard.writeText(event.customFields?.link || ''); props.setToastState({ open: true, message: 'Payment link copied!' }) }} className="gbk-icon-span link-icon" style={{ marginLeft: '10px' }}>
                              <ContentCopyRounded />
                            </span>
                          ) : ''}
                          <br />
                          <span className="fs-14">{`₹${Number(event.value).toFixed(2)}`}</span>
                          {event.customFields?.paymentTypes ? (
                            <><br /><span style={{ fontWeight: 'normal', fontSize: '0.8rem' }}>{formatPaymentTypes(event.customFields.paymentTypes)}</span></>
                          ) : ''}
                        </Fragment>
                      ) : ''}
                    </b>
                    {event.eventType === OrderEvent.PAYMENT ? <span className="fs-14"><br />₹ {Number(event.value).toFixed(2)}</span> : ''}
                    {event.comment && !isCommentRedundantWithPaymentTypes(event)
                      ? <span className="fs-14">
                        <br />
                        {event.comment}
                        {event.eventType === OrderEvent.AMOUNT_UPDATED ? (
                          (event?.customFields?.before ? ` from ₹ ${event?.customFields?.before} to ₹ ${event?.customFields?.after}` : `: ${event?.customFields?.after}`)
                        ) : ''}
                      </span>
                      : ''}
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </label>
        </div>

      </div>
      <div style={{ height: '80px', width: '10px' }}></div>
    </Paper>;
  }

  // Payment details
  function renderPaymentDetails() {

    // Caclulations
    const cancellationDeduction = (Math.max(props.order.refundAmounts?.vendorRefundSplitAmount || 0, 0) + Math.max(props.order.refundAmounts?.vendorDebitAmount || 0, 0)) + props.order.offlineCashAmount;
    const finalPayableAmount = props.order.finalVendorAmount - cancellationDeduction;

    return <Paper className="gbk-paper booking-detail" sx={{ width: 'calc(100% - 2px)' }} elevation={0}>
      <div className="content">

        {/* Payment Details */}
        <Box>
          <Box sx={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', alignItems: isPhone ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isPhone ? '8px' : undefined }}>
            <Typography className="bd-pay-heading" fontSize={isPhone ? 16 : 18} fontWeight={500}>Payment Details</Typography>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', width: isPhone ? '100%' : undefined }}>
              {isPhone && showCustomPaymentLink ? (
                <CreateCustomPaymentLink order={props.order} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    className="thick-border no-text-transform action-btn"
                    variant="outlined"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: isPhone ? '10px' : undefined, padding: isPhone ? '4px 8px' : undefined, minWidth: 0 }}
                  >
                    Create Payment Link
                  </Button>
                </CreateCustomPaymentLink>
              ) : ''}
              {isPhone && showRecordCashPayment ? (
                <RecordCashPayment order={props.order} backFn={() => props.backFn(true, props.order.id)} isSubscription={isSubscriptionOrder}>
                  <Button
                    className="thick-border no-text-transform action-btn"
                    variant="outlined"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: isPhone ? '10px' : undefined, padding: isPhone ? '4px 8px' : undefined, minWidth: 0 }}
                  >
                    Record Cash Payment
                  </Button>
                </RecordCashPayment>
              ) : ''}
              {isPhone && showDamageInvoice ? (
                <DamageInvoice order={props.order} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    className="thick-border no-text-transform action-btn"
                    variant="outlined"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: isPhone ? '10px' : undefined, padding: isPhone ? '4px 8px' : undefined, minWidth: 0 }}
                  >
                    Damage Invoice
                  </Button>
                </DamageInvoice>
              ) : ''}
              {isPhone && showCancelBooking ? (
                <CancelBooking order={props.order} backFn={() => props.backFn(true, props.order.id)}>
                  <Button
                    className="thick-border no-text-transform action-btn"
                    variant="outlined"
                    size="small"
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: isPhone ? '10px' : undefined, padding: isPhone ? '4px 8px' : undefined, minWidth: 0 }}
                  >
                    Cancel Booking
                  </Button>
                </CancelBooking>
              ) : ''}
            </Box>
          </Box>
          {!isPhone && isSubscriptionOrder && (
            <Box sx={{ fontSize: '14px', marginTop: '6px' }}>
              <span style={{ color: '#888', fontWeight: 700 }}>Outstanding: </span>
              <strong className="roboto" style={{ color: Colors.primary }}>₹ {Number(props.order.outstandingAmount || 0).toFixed(2)}</strong>
              <span style={{ margin: '0 10px', color: '#ccc' }}>|</span>
              <span style={{ color: '#888', fontWeight: 700 }}>Next Billing: </span>
              <strong>{props.order.nextBillingDate ? DateTime.fromMillis(Number(props.order.nextBillingDate)).toFormat('MMM d, yyyy') : '-'}</strong>
            </Box>
          )}
          <div className="gbk-well" style={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', alignItems: isPhone ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isPhone ? '12px' : '10px', padding: 20, marginTop: 10, fontSize: isPhone ? 14 : 16 }} >
            <div>
              Method of Payment: &nbsp;<b>{props.order.paymentMode}</b>
              {
                props.order.paymentMode !== PaymentMode.ONLINE && props.order.bookingStatus !== BookingStatus.CANCELLED ? (
                  <span>
                    (Cash to be collected offline: <b><span className="roboto">₹</span>{props.order.offlineCashAmount.toFixed(2)}</b>)
                  </span>
                ) : ''
              }
              <br />
              {
                props.order.paymentMode === PaymentMode.ONLINE ? (
                  <Fragment>
                    Payment Status: &nbsp;
                    <b style={{ color: props.order.paymentDone ? Colors.successGreen : Colors.failureRed }}>
                      {props.order.paymentDone ? 'Paid' : 'Not Paid'}
                    </b>
                    <br />
                  </Fragment>
                ) : ''
              }
              Refundable Security Deposit: <b className="roboto"> ₹ {props.order.depositAmount}</b>
            </div>
            {
              (!props.order.paymentDone) ? (
                <div>
                  <LoadingButton
                    size="small"
                    startIcon={<Refresh />}
                    variant={'outlined'}
                    className="thick-border"
                    loading={Boolean(localState.requestingReview)}
                    onClick={async () => {
                      setLocalState({ requestingReview: true });
                      try {
                        await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/resend-payment-link-sms`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                        props.backFn(true, props.order.id);
                      } catch (e: any) {
                        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                        setLocalState({ message: errorMessage })
                      }
                    }}
                  >
                    Send payment link again
                  </LoadingButton>
                </div>
              ) : ''
            }

          </div>
          <ul className="gbk-joined-list">
            <li>
              <div className="float-left">Vehicle Rental</div>
              <div className="float-right roboto">₹ {props.order.totalBikeAmount}</div>
            </li>
            <li hidden={props.order.totalAccessoryAmount < Number.EPSILON}>
              <div className="float-left">Accessory Rental</div>
              <div className="float-right roboto">₹ {props.order.totalAccessoryAmount}</div>
            </li>
            {(() => {
              const kmAmount = props.order.kmPackagePriceDetails?.reduce((s, d) => s + d.charge, 0) ?? 0;
              return kmAmount > Number.EPSILON ? (
                <li>
                  <div className="float-left">Extra KM Charges</div>
                  <div className="float-right roboto">₹ {kmAmount.toFixed(2)}</div>
                </li>
              ) : null;
            })()}
            <li hidden={props.order.defaultDiscount < Number.EPSILON}>
              <div className="float-left">Default discount offered by dealer</div>
              <div className="float-right roboto" style={{ color: Colors.primary }}>- ₹ {props.order.defaultDiscount}</div>
            </li>
            <li hidden={(props.order.vendorDiscount - props.order.defaultDiscount) < Number.EPSILON}>
              <div className="float-left">Coupon discount offered by dealer</div>
              <div className="float-right roboto" style={{ color: Colors.primary }}>- ₹ {props.order.vendorDiscount - props.order.defaultDiscount}</div>
            </li>
            <li>
              <div className="float-left bold">Total amount considerable for settlement (A)</div>
              <div className="float-right roboto bold">₹ {props.order.totalBikeAmount + props.order.totalAccessoryAmount - props.order.vendorDiscount}</div>
            </li>
            <li hidden={props.order.gobikesDiscount < Number.EPSILON}>
              <div className="float-left">Coupon discount offered by GoWheelo</div>
              <div className="float-right roboto" style={{ color: Colors.primary }}>- ₹ {props.order.gobikesDiscount}</div>
            </li>
            <li hidden={props.order.goCoinsUsed < Number.EPSILON}>
              <div className="float-left">GoCoins discount offered by GoWheelo</div>
              <div className="float-right roboto" style={{ color: Colors.primary }}>- ₹ {props.order.goCoinsUsed}</div>
            </li>
          </ul>
        </Box>

        {/* Deductions */}
        <Box>
          <Typography className="bd-pay-heading" fontSize={isPhone ? 16 : 18} fontWeight={500} paddingTop={2}>Deductions</Typography>
          <ul className="gbk-joined-list">
            {/* <li>
              <div className="float-left">GoWheelo Commission (including GST)</div>
              <div className="float-right roboto">₹ {(props.order.goBikesCommission + props.order.convFees).toFixed(2)}</div>
            </li> */}
            <li hidden={props.order.tdrAmount < Number.EPSILON}>
              <div className="float-left">TDR</div>
              <div className="float-right roboto">₹ {props.order.tdrAmount.toFixed(2)}</div>
            </li>
            <li>
              <div className="float-left bold">{props.order.goBikesCommission > Number.EPSILON ? 'GoWheelo Commission (including GST and Payment Gateway Charges)' : 'Payment Gateway Charges'} (B)</div>
              <div className="float-right roboto bold">₹ {(props.order.goBikesCommission + props.order.tdrAmount + props.order.convFees).toFixed(2)}</div>
            </li>
            <li hidden={props.order.gobikesDiscount + props.order.goCoinsUsed < Number.EPSILON}>
              <div className="float-left">Final GoWheelo Commission<br />(Commission - Discount offered by GoWheelo)</div>
              <div className="float-right roboto">₹ {(props.order.goBikesCommission + props.order.tdrAmount + props.order.convFees - props.order.gobikesDiscount - props.order.goCoinsUsed).toFixed(2)}</div>
            </li>
          </ul>
        </Box>

        {/* Settlement details */}
        <Box>
          <Typography className="bd-pay-heading" fontSize={isPhone ? 16 : 18} fontWeight={500} paddingTop={2}>Settlement Details</Typography>
          <ul className="gbk-joined-list">
            <li hidden={!(props.order.paymentMode !== PaymentMode.OFFLINE)}>
              <div className="float-left">Amount{props.order.paymentDone ? ' ' : ' to be '}paid online by customer</div>
              <div className="float-right roboto">₹ {props.order.amountPayableOnline.toFixed(2)}</div>
            </li>
            <li hidden={!(props.order.paymentMode !== PaymentMode.ONLINE)}>
              <div className="float-left bold">Cash to be collected OFFLINE</div>
              <div className="float-right roboto bold">₹ {props.order.offlineCashAmount.toFixed(2)}</div>
            </li>
            <li hidden={!(props.order.paymentMode !== PaymentMode.OFFLINE)}>
              <div className={`float-left ${props.order.bookingStatus === BookingStatus.CANCELLED ? '' : 'bold'}`}>
                {props.order.bookingStatus === BookingStatus.CANCELLED ? '' : 'Final '}Payable Amount (A-B)
              </div>
              <div className={`float-right roboto ${props.order.bookingStatus === BookingStatus.CANCELLED ? '' : 'bold'}`}>₹ {props.order.finalVendorAmount.toFixed(2)}</div>
            </li>
            <li hidden={!(props.order.bookingStatus === BookingStatus.CANCELLED && props.order.refundAmounts && (props.order.refundAmounts.vendorDebitAmount || props.order.refundAmounts.vendorRefundSplitAmount))}>
              <div className="float-left">Cancellation deduction</div>
              <div className="float-right roboto">- ₹ {cancellationDeduction.toFixed(2)}</div>
            </li>
            <li hidden={!(props.order.bookingStatus === BookingStatus.CANCELLED && props.order.refundAmounts && (props.order.refundAmounts.vendorDebitAmount || props.order.refundAmounts.vendorRefundSplitAmount))}>
              <div className="float-left bold">Final Payable Amount</div>
              <div className="float-right roboto bold"> ₹ {finalPayableAmount.toFixed(2)}</div>
            </li>
            <li hidden={props.order.depositAmount < Number.EPSILON} className="bd-deposit-row">
              <div className="float-left">Security Deposit</div>
              <div className="float-right roboto">₹ {props.order.depositAmount}</div>
            </li>
            {/* <li hidden={!(props.order.paymentMode === PaymentMode.ONLINE)}>
              <div className="float-left">Settlement Status</div>
              <div className="float-right roboto bold" style={{ color: props.order.settlementStatus && props.order.settlementStatus === SettlementStatus.SETTLED ? Colors.successGreen : Colors.failureRed }}>
                {props.order.settlementStatus ? props.order.settlementStatus : SettlementStatus.PG_SETTLEMENT_PENDING}
              </div>
            </li>
            <li hidden={!(props.order.paymentMode === PaymentMode.ONLINE)}>
              <div className="float-left">Settlement Date and Time</div>
              <div className="float-right roboto">{props.order.settlementTime ? DateTime.fromMillis(Number(props.order.settlementTime)).toLocaleString(DateTime.DATETIME_MED) : '-'}</div>
            </li> */}
          </ul>
        </Box>

        {/* Bike update details */}
        <Box sx={{ ...(!props.order.bikeUpdateDetails && { display: 'none' }) }}>
          <Typography className="bd-pay-heading" fontSize={isPhone ? 16 : 18} fontWeight={500} paddingTop={2}>Bike Change Details</Typography>
          <ul className="gbk-joined-list">
            <li>
              <div className="float-left">Payable amount before bike change</div>
              <div className="float-right roboto">₹ {props.order.totalPayableAmount.toFixed(2)}</div>
            </li>
            {props.order.bikeUpdateDetails?.map((details, i) => (
              <Fragment key={i}>
                <li>
                  <div className="float-left">
                    {
                      details.newPayableAmount < details.prevTotalPayableAmount
                        ? 'Amount to be refunded offline for bike change'
                        : 'Amount to be collected offline for bike change'
                    }
                  </div>
                  <div
                    className="float-right roboto"
                    style={{ color: details.newPayableAmount < details.prevTotalPayableAmount ? Colors.failureRed : Colors.primaryDark }}
                  >
                    {details.newPayableAmount < details.prevTotalPayableAmount ? '- ' : ''}
                    ₹ {Math.abs(details.newPayableAmount - details.prevTotalPayableAmount).toFixed(2)}</div>
                </li>
                {i === (props.order.bikeUpdateDetails?.length || 0) - 1 ? (
                  <li>
                    <div className="float-left"><b>Payable amount after bike change</b></div>
                    <div className="float-right roboto"><b>₹ {(details.newPayableAmount || 0).toFixed(2)}</b></div>
                  </li>
                ) : ''}
              </Fragment>
            ))}
          </ul>
        </Box>

      </div>
      <br />
      <br />
      {showBottomPanel ? <span><br /><br /><br /></span> : ''}
    </Paper>;
  }

  // Render timeline
  function renderTimeline() {
    return <div className="gbk-field" style={{ width: '100%' }} hidden={!props.order.bikeDetails}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label className="label">Booking Timeline</label>
        <Select size="small" value={timelineFilter} onChange={(e) => setTimelineFilter(e.target.value)}>
          {getAvailableTimelineFilterOptions(props.order.orderTrail).map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </div>
      <br />
      <label className="value" style={{ width: '100%' }}>
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.5,
              fontSize: isPhone ? '12px' : '14px',
            },
          }}
        >
          {filterOrderTrail(props.order.orderTrail, timelineFilter).map((event, i) => (
            <TimelineItem key={i}>
              <TimelineOppositeContent color="textSecondary">
                {toIstDateString(event.time, { removeDay: true, returnJsx: true })}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={orderEventToDisplayMapping[event.eventType].nodeColor || 'primary'} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <b>
                  <span className="fs-14">
                    {orderEventToDisplayMapping[event.eventType].text}
                  </span>
                  {(event.eventType === OrderEvent.PAYMENT_LINK || event.eventType === OrderEvent.CUSTOM_PAYMENT_LINK || event.eventType === OrderEvent.OFFLINE_PAYMENT || event.eventType === OrderEvent.DAMAGE_INVOICE) && event.value ? (
                    <Fragment>
                      {event.customFields?.link ? (
                        <span onClick={() => { navigator.clipboard.writeText(event.customFields?.link || ''); props.setToastState({ open: true, message: 'Payment link copied!' }) }} className="gbk-icon-span link-icon" style={{ marginLeft: '10px' }}>
                          <ContentCopyRounded />
                        </span>
                      ) : ''}
                      <br />
                      <span className="fs-14">{`₹${Number(event.value).toFixed(2)}`}</span>
                    </Fragment>
                  ) : ''}
                </b>
                {event.eventType === OrderEvent.PAYMENT ? <span className="fs-14"><br />₹ {Number(event.value).toFixed(2)}</span> : ''}
                {event.comment
                  ? <span className="fs-14">
                    <br />
                    {event.comment}
                    {event.eventType === OrderEvent.AMOUNT_UPDATED ? (
                      event?.customFields?.before
                        ? ` from ₹ ${event?.customFields?.before} to ₹ ${event?.customFields?.after}`
                        : `: ₹ ${event?.customFields?.after}`
                    ) : ''}
                  </span>
                  : ''}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </label>
    </div>
  }

  // Render extra gap if showing action buttons
  function renderExtraGap() {
    return showBottomPanel && isPhone ? <div style={{ height: "130px", width: "10px" }}></div> : "";
  }

  // Download file from server
  async function viewProfilePic() {
    const latestPicDoc = props.order.user?.reputeAttributes?.latestPic?.doc;
    if (!latestPicDoc) return;
    const docName = Object.keys(latestPicDoc)[0];
    if (!docName) return;
    try {
      const response = await GobikesApi.get(
        `/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/docs/latestPic/${docName}`,
        { headers: { 'vendor-token': authCookie }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      setProfilePicUrl(url);
    } catch (e: any) {
      const errorMessage = e.response?.data?.message ? 'Error: ' + e.response.data.message : 'Failed to load profile picture';
      setLocalState({ message: errorMessage });
    }
  }

  async function downloadFile(urlSuffix: string, filename: string) {
    try {
      const response = await GobikesApi.get(
        `/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/repute/docs/${urlSuffix}`,
        { headers: { "vendor-token": authCookie }, responseType: "blob" }
      );
      resolveAndDownloadBlob(response, filename);
    } catch (e: any) {
      console.log(e);
      const errorMessage =
        e.response && e.response.data && e.response.data.message
          ? "Error: " + e.response.data.message
          : "An error occured";
      setLocalState({ message: errorMessage });
    }
  }

  // Download file from data
  function resolveAndDownloadBlob(response: any, filename: string) {
    filename = decodeURI(filename);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    link.remove();
  }
}
